import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "../styles/AdminSubscribers.css";

import {
  SUBSCRIBERS_API,
  PAYMENT_API,
  DOWNLOAD_TEMPLATE_API,
  IMPORT_SUBSCRIBERS_API,
  EXPORT_SUBSCRIBERS_API,
  PLANS_API,
  WHATSAPP_API,
  getImageUrl,
} from "../config/api";
import { showConfirm } from "../utils/customDialog";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const parseDateLocal = (val) => {
  if (!val) return null;
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return null;
    return { year: val.getFullYear(), month: val.getMonth() + 1, day: val.getDate() };
  }
  const str = String(val).trim();

  // 1. Textual month format: "September 2026", "Sept 2026", "Oct 2026"
  const monthNames = ["january","february","march","april","may","june","july","august","september","october","november","december"];
  const words = str.split(/[\s,]+/);
  let textM = null;
  let textY = null;
  for (const w of words) {
    const idx = monthNames.findIndex(mn => mn.startsWith(w.toLowerCase().slice(0, 3)));
    if (idx !== -1) textM = idx + 1;
    if (/^\d{4}$/.test(w)) textY = parseInt(w, 10);
  }
  if (textM && textY) {
    return { year: textY, month: textM, day: 1 };
  }

  // 2. Timestamp with 23:59:59 (e.g. "2026-08-31T23:59:59.000Z" or "2026-08-31 23:59:59"):
  // In UTC+5:30 (IST), 23:59:59Z rolls over to 05:29:59 next morning (next month).
  // We extract the intended calendar date.
  const match2359 = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T\s]23:59/);
  if (match2359) {
    return {
      year: parseInt(match2359[1], 10),
      month: parseInt(match2359[2], 10),
      day: parseInt(match2359[3], 10)
    };
  }

  // 3. Pure YYYY-MM-DD or YYYY-MM
  const matchYmd = str.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (matchYmd) {
    return {
      year: parseInt(matchYmd[1], 10),
      month: parseInt(matchYmd[2], 10),
      day: matchYmd[3] ? parseInt(matchYmd[3], 10) : 1
    };
  }

  // 4. Standard Date parsing in local timezone
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate()
    };
  }
  return null;
};

const parseMonthYearFromValue = (val) => {
  const p = parseDateLocal(val);
  if (p) return { month: p.month, year: p.year };
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

const AdminSubscribers = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  const [viewingReceiptSub, setViewingReceiptSub] = useState(null);

  // Tab state: "directory", "broadcast", "bulk-import"
  const [activeTab, setActiveTab] = useState("directory");

  // ================= EXPORT STATES =================
  const [exportLoading, setExportLoading] = useState(false);
  const [exportTypeLoading, setExportTypeLoading] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);

  // ================= BULK IMPORT STATES =================
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  // ================= BROADCAST NOTIFICATIONS STATES =================
  const [broadcastCategory, setBroadcastCategory] = useState("all");
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState(null);

  // ================= BULK NOTICE ALERTS STATES =================
  const [bulkWarningCategory, setBulkWarningCategory] = useState("all");
  const [bulkWarningType, setBulkWarningType] = useState("1 Month Before");
  const [bulkWarningSending, setBulkWarningSending] = useState(false);
  const [bulkWarningStatus, setBulkWarningStatus] = useState(null);

  // ================= VIEW & EDIT SUBSCRIBER STATES =================
  const [viewingSub, setViewingSub] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [editForm, setEditForm] = useState({
    subscription_id: "",
    login_id: "",
    user_name: "",
    user_email: "",
    user_phone: "",
    amount: "",
    start_date: "",
    end_date: "",
    status: "",
    plan_id: "",
    order_id: "",
  });

  // ================= POD TRACKING STATES =================
  const [podModalSub, setPodModalSub] = useState(null);
  const [podForm, setPodForm] = useState({
    pod_number: "",
    courier_partner: "India Post (Speed Post)",
    tracking_url: "",
    notify_user: true,
  });
  const [savingPod, setSavingPod] = useState(false);

  const handleOpenPodModal = (sub) => {
    setPodModalSub(sub);
    setPodForm({
      pod_number: sub.pod_number || "",
      courier_partner: sub.courier_partner || getDispatchMode(sub),
      tracking_url: sub.tracking_url || "",
      notify_user: true,
    });
  };

  const handleSavePod = async (e) => {
    e.preventDefault();
    if (!podModalSub) return;
    setSavingPod(true);

    try {
      const res = await fetch(`${SUBSCRIBERS_API}/update_pod`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: podModalSub.id,
          ...podForm,
        }),
      });
      const data = await res.json();
      if (data?.status === "success") {
        showPopup(data.message || "POD details updated successfully!");
        setPodModalSub(null);
        fetchSubscribers();
      } else {
        showPopup(data?.message || "Failed to update POD", "error");
      }
    } catch (err) {
      showPopup("Error updating POD: " + err.message, "error");
    } finally {
      setSavingPod(false);
    }
  };

  // ================= 20-YEAR HISTORY MODAL STATES =================
  const [historySub, setHistorySub] = useState(null);
  const [subHistoryList, setSubHistoryList] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const handleOpenHistory = async (sub) => {
    setHistorySub(sub);
    setLoadingHistory(true);
    try {
      const res = await fetch(`${SUBSCRIBERS_API}/history?user_id=${sub.user_id || ""}&login_id=${sub.login_id || ""}`);
      const data = await res.json();
      if (data?.status === "success") {
        setSubHistoryList(data.history || []);
      } else {
        setSubHistoryList([]);
      }
    } catch (err) {
      console.error("Failed to load history:", err);
      setSubHistoryList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatSubStartMonth = (startDate, longFormat = false) => {
    if (!startDate) return "N/A";
    const p = parseDateLocal(startDate);
    if (!p) return "N/A";
    const mObj = MONTH_OPTIONS.find(m => m.value === p.month);
    const mLabel = longFormat ? mObj?.label : (mObj?.label?.slice(0, 4) === "Sept" ? "Sept" : mObj?.label?.slice(0, 3));
    return `${mLabel} ${p.year}`;
  };

  const formatSubEndMonth = (startDate, endDate, isAnnual = 0, longFormat = false) => {
    let pEnd = endDate ? parseDateLocal(endDate) : null;
    let pStart = startDate ? parseDateLocal(startDate) : null;

    if (pEnd && pStart) {
      const monthDiff = (pEnd.year - pStart.year) * 12 + (pEnd.month - pStart.month);
      if (monthDiff > 0 && monthDiff % 12 === 0) {
        let newM = pEnd.month - 1;
        let newY = pEnd.year;
        if (newM < 1) {
          newM = 12;
          newY -= 1;
        }
        pEnd = { year: newY, month: newM };
      }
    } else if (!pEnd && pStart) {
      const addMonths = Number(isAnnual) === 1 ? 23 : 11;
      const totalM = (pStart.month - 1) + addMonths;
      pEnd = {
        month: (totalM % 12) + 1,
        year: pStart.year + Math.floor(totalM / 12)
      };
    }

    if (!pEnd) return "N/A";
    const mObj = MONTH_OPTIONS.find(m => m.value === pEnd.month);
    const mLabel = longFormat ? mObj?.label : (mObj?.label?.slice(0, 4) === "Sept" ? "Sept" : mObj?.label?.slice(0, 3));
    return `${mLabel} ${pEnd.year}`;
  };

  const formatSubPeriod = (startDate, endDate, isAnnual = 0, separator = " — ", longFormat = false, subObj = null) => {
    if (subObj) {
      const term = getSubscriberTermDates(subObj, longFormat);
      if (term.startStr !== "N/A" && term.endStr !== "N/A") {
        return `${term.startStr}${separator}${term.endStr}`;
      }
    }
    const startStr = formatSubStartMonth(startDate, longFormat);
    const endStr = formatSubEndMonth(startDate, endDate, isAnnual, longFormat);
    if (startStr === "N/A" || endStr === "N/A") return "N/A";
    return `${startStr}${separator}${endStr}`;
  };

  /* ================= ACCURATE TERM DATES (SEPARATING CURRENT TERM FROM HISTORICAL JOIN DATE) ================= */
  const getSubscriberTermDates = (sub, longFormat = false) => {
    if (!sub) return { startStr: "N/A", endStr: "N/A", memberSinceStr: null };

    const pkg = (sub.package_name || "").toUpperCase();
    const amt = Number(sub.amount || 0);

    const is4Years = pkg.includes("4 YEAR") || 
                     pkg.includes("4-YEAR") || 
                     pkg.includes("FOUR YEAR") ||
                     pkg.includes("FOURYEAR") ||
                     pkg.includes("4YEAR") ||
                     amt >= 2400;

    const is3Years = !is4Years && (
                     pkg.includes("3 YEAR") ||
                     pkg.includes("3-YEAR") ||
                     pkg.includes("THREE YEAR") ||
                     pkg.includes("THREEYEAR") ||
                     pkg.includes("3YEAR") ||
                     amt === 1800);

    const is2Years = !is4Years && !is3Years && (
                     pkg.includes("2 YEAR") || 
                     pkg.includes("2-YEAR") || 
                     pkg.includes("TWO YEAR") ||
                     pkg.includes("TWOYEAR") ||
                     pkg.includes("2YEAR") ||
                     Number(sub.is_annual) === 1 ||
                     amt === 1200);

    const termMonths = is4Years ? 48 : (is3Years ? 36 : (is2Years ? 24 : 12));

    let startStr = "N/A";
    let endStr = "N/A";
    let memberSinceStr = null;

    // 1. Check explicit end_month string or compute using formatSubEndMonth
    if (sub.end_month && String(sub.end_month).trim() && !/^\d{4}-\d{2}/.test(String(sub.end_month).trim())) {
      endStr = String(sub.end_month).trim();
    } else if (sub.end_date) {
      endStr = formatSubEndMonth(sub.start_date, sub.end_date, is2Years ? 1 : 0, longFormat);
    }

    // 2. Check explicit start_month string or compute current subscription term start
    if (sub.start_month && String(sub.start_month).trim() && !/^\d{4}-\d{2}/.test(String(sub.start_month).trim())) {
      startStr = String(sub.start_month).trim();
    } else if (sub.end_date || endStr !== "N/A") {
      // Determine if sub.start_date is from the CURRENT term or is an ancient historical join date
      let isCurrentStartDate = false;
      if (sub.start_date && sub.end_date) {
        const pS = parseDateLocal(sub.start_date);
        const pE = parseDateLocal(sub.end_date);
        if (pS && pE) {
          const monthSpan = (pE.year - pS.year) * 12 + (pE.month - pS.month);
          if (monthSpan <= termMonths + 2 && monthSpan >= termMonths - 2) {
            isCurrentStartDate = true;
          }
        }
      }

      if (isCurrentStartDate && sub.start_date) {
        startStr = formatSubStartMonth(sub.start_date, longFormat);
      } else if (endStr !== "N/A" || sub.end_date) {
        const pE = (endStr !== "N/A") ? parseMonthYearFromValue(endStr) : parseDateLocal(sub.end_date);
        if (pE && pE.month && pE.year) {
          const totalMonths = (pE.year * 12) + (pE.month - 1) - (termMonths - 1);
          const calcY = Math.floor(totalMonths / 12);
          const calcM = ((totalMonths % 12) + 12) % 12 + 1;
          const mObj = MONTH_OPTIONS.find(m => m.value === calcM);
          const mLabel = longFormat ? mObj?.label : (mObj?.label?.slice(0, 4) === "Sept" ? "Sept" : mObj?.label?.slice(0, 3) || `Month ${calcM}`);
          startStr = `${mLabel} ${calcY}`;
        }
      }
    } else if (sub.start_date) {
      startStr = formatSubStartMonth(sub.start_date, longFormat);
      if (endStr === "N/A") {
        const pS = parseDateLocal(sub.start_date);
        if (pS) {
          const totalMonths = (pS.year * 12) + (pS.month - 1) + (termMonths - 1);
          const calcY = Math.floor(totalMonths / 12);
          const calcM = ((totalMonths % 12) + 12) % 12 + 1;
          const mObj = MONTH_OPTIONS.find(m => m.value === calcM);
          const mLabel = longFormat ? mObj?.label : (mObj?.label?.slice(0, 4) === "Sept" ? "Sept" : mObj?.label?.slice(0, 3) || `Month ${calcM}`);
          endStr = `${mLabel} ${calcY}`;
        }
      }
    }

    // Magazine validity period rule: Start and End month can NEVER have identical month name (e.g. Sept to Sept = 13 issues)
    if (startStr !== "N/A" && endStr !== "N/A") {
      const pS = parseMonthYearFromValue(startStr);
      const pE = parseMonthYearFromValue(endStr);
      if (pS.month === pE.month) {
        const addMonths = termMonths - 1;
        const newEM = ((pS.month - 1 + addMonths) % 12) + 1;
        const newEY = pS.year + Math.floor((pS.month - 1 + addMonths) / 12);
        const mObj = MONTH_OPTIONS.find(m => m.value === newEM);
        const mLabel = longFormat ? mObj?.label : (mObj?.label?.slice(0, 3) || `Month ${newEM}`);
        endStr = `${mLabel} ${newEY}`;
      }
    }

    // Check lifetime joining date / Member Since
    const initialDateStr = sub.first_joined_date || sub.start_date;
    if (initialDateStr) {
      const initD = new Date(initialDateStr);
      if (!isNaN(initD.getTime())) {
        const initYear = initD.getFullYear();
        const initMonth = initD.getMonth() + 1;
        const pS = parseMonthYearFromValue(startStr);
        // Only show Member Since if the initial join year is earlier than the current term start year and valid (>= 1990)
        const isEarlier = pS.year && (initYear < pS.year || (initYear === pS.year && pS.month && (pS.month - initMonth >= 6)));
        if (isEarlier && initYear >= 1990) {
          memberSinceStr = initD.toLocaleDateString("en-IN", { month: longFormat ? "long" : "short", year: "numeric" });
        }
      }
    }

    return { startStr, endStr, memberSinceStr };
  };

  /* ================= ACCURATE RECEIPT PLAN INFO ================= */
  const getReceiptPlanInfo = (item) => {
    if (!item) return { is2Years: false, is4Years: false, is3Years: false, title: "Subscription", billingTerm: "1 Year (12 Issues)", issuesCount: 12 };
    const p = plans.find(plan => Number(plan.id) === Number(item.plan_id));
    const pkg = (item.package_name || "").toUpperCase();
    const amt = Number(item.amount || 0);
    let is4Years = false;
    let is3Years = false;
    let is2Years = false;
    
    if (pkg.includes("4 YEAR") || pkg.includes("4-YEAR") || pkg.includes("FOUR YEAR") || pkg.includes("FOURYEAR") || pkg.includes("4YEAR") || amt >= 2400) {
      is4Years = true;
      is2Years = true;
    } else if (pkg.includes("3 YEAR") || pkg.includes("3-YEAR") || pkg.includes("THREE YEAR") || pkg.includes("THREEYEAR") || pkg.includes("3YEAR") || amt === 1800) {
      is3Years = true;
    } else if (pkg.includes("2 YEAR") || pkg.includes("2-YEAR") || pkg.includes("TWO YEAR") || pkg.includes("TWOYEAR") || pkg.includes("2YEAR") || amt === 1200) {
      is2Years = true;
    } else if (pkg.includes("1 YEAR") || pkg.includes("1-YEAR") || pkg.includes("ONE YEAR") || pkg.includes("ONEYEAR") || pkg.includes("1YEAR") || amt === 600 || amt === 650) {
      is2Years = false;
    } else if (Number(item.is_annual) === 1) {
      is2Years = true;
    } else if (p && amt === Number(p.annual_price)) {
      is2Years = true;
    } else if (item.start_date && item.end_date) {
      const s = new Date(item.start_date);
      const e = new Date(item.end_date);
      const mDiff = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
      if (mDiff >= 40 && mDiff <= 52 && amt >= 2000) {
        is4Years = true;
        is2Years = true;
      } else if (mDiff >= 30 && mDiff <= 38 && amt >= 1500) {
        is3Years = true;
      } else if (mDiff >= 20 && mDiff <= 28) {
        is2Years = true;
      }
    }
    
    const title = item.package_name || (p ? (is4Years ? "4 YEARS SUBSCRIPTION + GENERAL POST" : is3Years ? "3 YEARS SUBSCRIPTION + GENERAL POST" : is2Years ? p.annual_title : p.monthly_title) : (is4Years ? "4 YEARS SUBSCRIPTION" : is3Years ? "3 YEARS SUBSCRIPTION" : is2Years ? "2 YEARS SUBSCRIPTION" : "1 YEAR SUBSCRIPTION"));
    const billingTerm = is4Years ? "4 Years (48 Issues)" : is3Years ? "3 Years (36 Issues)" : is2Years ? "2 Years (24 Issues)" : "1 Year (12 Issues)";
    const issuesCount = is4Years ? 48 : is3Years ? 36 : is2Years ? 24 : 12;
    return { is2Years, is4Years, is3Years, title, billingTerm, issuesCount };
  };

  const handleOpenReceiptForSub = async (sub) => {
    // 1. If subscription is pending approval or awaiting activation, preview the current transaction voucher directly
    if (Number(sub.receipt_approved) !== 1 || (sub.status || "").toLowerCase() === "pending") {
      setViewingReceiptSub({
        ...sub,
        receipt_number: sub.order_id || sub.payment_id || `PENDING-APPROVAL`,
        receipt_date: sub.created_at || new Date().toISOString(),
        package_name: sub.package_name || "1 YEAR SUBSCRIPTION + GENERAL POST",
        amount: sub.amount || 650.00,
        platform_fee: sub.platform_fee !== undefined ? sub.platform_fee : 50.00,
        payment_id: sub.payment_id || sub.order_id,
        start_date: sub.start_date,
        end_date: sub.end_date,
        start_month: sub.start_month,
        end_month: sub.end_month,
        overall_start_month: sub.start_month,
        overall_end_month: sub.end_month,
        is_pending_voucher: true,
      });
      return;
    }

    try {
      const res = await fetch(`${SUBSCRIBERS_API}/history?user_id=${sub.user_id || ""}&login_id=${sub.login_id || ""}`);
      const data = await res.json();
      if (data.status === "success" && data.history && data.history.length > 0) {
        // Find exact matching historical receipt by order_id, payment_id, or receipt_number
        const match = data.history.find(h => 
          (h.order_id && sub.receipt_number && h.order_id.trim().toLowerCase() === sub.receipt_number.trim().toLowerCase()) ||
          (h.payment_id && sub.receipt_number && h.payment_id.trim().toLowerCase() === sub.receipt_number.trim().toLowerCase()) ||
          (h.order_id && sub.order_id && h.order_id.trim().toLowerCase() === sub.order_id.trim().toLowerCase()) ||
          (h.payment_id && sub.payment_id && h.payment_id.trim().toLowerCase() === sub.payment_id.trim().toLowerCase())
        ) || data.history.find(h => h.status === "paid") || data.history[0];

        if (match) {
          setViewingReceiptSub({
            ...sub,
            ...match,
            receipt_number: match.order_id || sub.receipt_number || `VS-RCP-${String(match.id).padStart(5, "0")}`,
            receipt_date: match.receipt_date || match.created_at || sub.receipt_date,
            package_name: match.package_name || sub.package_name,
            amount: match.amount || sub.amount,
            platform_fee: match.platform_fee !== undefined && match.platform_fee !== null ? match.platform_fee : sub.platform_fee,
            start_date: match.start_date || sub.start_date,
            end_date: match.end_date || sub.end_date,
            start_month: match.start_month || sub.start_month,
            end_month: match.end_month || sub.end_month,
            overall_start_month: sub.start_month,
            overall_end_month: sub.end_month,
            history_count: data.history.length
          });
          return;
        }
      }
    } catch (e) {
      console.warn("Failed to fetch receipt history, falling back to sub:", e);
    }
    setViewingReceiptSub(sub);
  };

  /* ================= DISPATCH MODE DETECTOR ================= */
  const getDispatchMode = (sub) => {
    if (!sub) return "Indian Postal / General Post";

    // 1. If explicit specific courier partner is recorded
    if (
      sub.courier_partner && 
      sub.courier_partner.trim() && 
      sub.courier_partner !== "Indian Postal / Speed Post" && 
      sub.courier_partner !== "India Post (Speed Post)"
    ) {
      return sub.courier_partner;
    }

    // 2. If backend computed dispatch_mode is present
    if (sub.dispatch_mode && sub.dispatch_mode.trim()) {
      return sub.dispatch_mode;
    }

    // 3. If Abroad
    if (Number(sub.is_abroad) === 1 || (sub.country && sub.country.toLowerCase() !== "india")) {
      return "Air Mail / International Post";
    }

    // 4. Check plan by ID
    const pid = Number(sub.plan_id);
    if (pid === 4) return "Indian Postal / Book Post";
    if (pid === 3) return "Indian Postal / Speed Post";
    if (pid === 2) return "Courier Service";
    if (pid === 1) return "Indian Postal / General Post";
    if (pid === 5) return "Direct / Hand Delivery";
    if (pid === 6) return "Air Mail / International Post";

    // 5. Check plan title or remarks
    const p = plans.find(pl => Number(pl.id) === pid);
    const text = (
      (p?.monthly_title || "") + " " +
      (p?.annual_title || "") + " " +
      (sub.package_name || "") + " " +
      (sub.remarks_1 || "") + " " +
      (sub.remarks_2 || "")
    ).toUpperCase();

    if (text.includes("BOOK POST")) return "Indian Postal / Book Post";
    if (text.includes("SPEED POST")) return "Indian Postal / Speed Post";
    if (text.includes("COURIER")) return "Courier Service";
    if (text.includes("DIRECT") || text.includes("HAND")) return "Direct / Hand Delivery";
    if (text.includes("GENERAL POST") || text.includes("ORDINARY POST")) return "Indian Postal / General Post";
    if (text.includes("ABROAD") || text.includes("OVERSEAS")) return "Air Mail / International Post";

    return "Indian Postal / General Post";
  };

  /* ================= NUMBER TO WORDS HELPER ================= */
  const convertNumberToWords = (amount) => {
    const num = Math.floor(Number(amount));
    if (!num || isNaN(num)) return "Rupees Zero Only";
    
    const single = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const double = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const formatHundreds = (n) => {
      let str = "";
      if (n > 99) {
        str += single[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }
      if (n > 19) {
        str += double[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + single[n % 10] : "");
      } else if (n > 0) {
        str += single[n];
      }
      return str.trim();
    };

    let wordStr = "";
    let rem = num;
    if (Math.floor(rem / 10000000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 10000000)) + " Crore ";
      rem %= 10000000;
    }
    if (Math.floor(rem / 100000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 100000)) + " Lakh ";
      rem %= 100000;
    }
    if (Math.floor(rem / 1000) > 0) {
      wordStr += formatHundreds(Math.floor(rem / 1000)) + " Thousand ";
      rem %= 1000;
    }
    if (rem > 0) {
      wordStr += formatHundreds(rem);
    }

    return "Indian Rupees " + wordStr.trim() + " Only";
  };

  // ================= FILTERS =================
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all"); // all | renewals | pending | active | expiring | expired_sub | abroad | local
  const [selectedMetricYear, setSelectedMetricYear] = useState(new Date().getFullYear());
  const [selectedYearFilter, setSelectedYearFilter] = useState("all");
  const [dateFilterPreset, setDateFilterPreset] = useState("all"); // all | today | yesterday | last_7_days | this_month | custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Helper to match dates according to selected preset or custom range
  const matchesDateFilter = (dateVal, preset, customStart, customEnd) => {
    if (preset === "all") return true;
    if (!dateVal) return false;
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return false;

    const toLocalDateStr = (dateObj) => {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const now = new Date();
    const dStr = toLocalDateStr(d);
    const todayStr = toLocalDateStr(now);

    if (preset === "today") {
      return dStr === todayStr;
    }
    if (preset === "yesterday") {
      const yest = new Date(now);
      yest.setDate(yest.getDate() - 1);
      return dStr === toLocalDateStr(yest);
    }
    if (preset === "last_7_days") {
      const start7 = new Date(now);
      start7.setDate(start7.getDate() - 6);
      start7.setHours(0, 0, 0, 0);
      const endToday = new Date(now);
      endToday.setHours(23, 59, 59, 999);
      return d >= start7 && d <= endToday;
    }
    if (preset === "this_month") {
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }
    if (preset === "custom") {
      if (customStart && dStr < customStart) return false;
      if (customEnd && dStr > customEnd) return false;
      return true;
    }
    return true;
  };

  // ================= PAGINATION =================
  const [currentPage, setCurrentPage] = useState(1);
  const subsPerPage = 15;

  // ================= POPUP / TOAST NOTIFICATION =================
  const [popup, setPopup] = useState(null);
  const showPopup = useCallback((msg, type = "success") => {
    setPopup({ msg, type });
    setTimeout(() => setPopup(null), 3000);
  }, []);

  // ================= FETCH SUBSCRIBERS =================
  const fetchSubscribers = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(SUBSCRIBERS_API);

      if (!res.ok) {
        if (res.status === 504 && retryCount < 1) {
          // Automatic 1-time retry for transient 504 timeout
          console.warn("[Subscribers] 504 Gateway Timeout encountered, auto-retrying in 2 seconds...");
          setTimeout(() => fetchSubscribers(retryCount + 1), 2000);
          return;
        }
        if (res.status === 504) {
          throw new Error("Server Gateway Timeout (HTTP 504). The subscribers database took longer than usual to respond. Please click 'Retry Loading' below.");
        }
        if (res.status === 502 || res.status === 503) {
          throw new Error(`Server temporarily unavailable (HTTP ${res.status}). The backend may be updating. Please retry in a moment.`);
        }
        throw new Error(`Server returned HTTP ${res.status}: ${res.statusText}`);
      }

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseErr) {
        throw new Error("Invalid response format received from server. Please click 'Retry Loading' below.");
      }

      if (data.status === "success") {
        setSubscriptions(data.subscriptions || []);
      } else {
        setError(data.message || "Failed to load subscribers");
      }
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(PLANS_API);
      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load plans:", err);
    }
  }, []);

  useEffect(() => {
    fetchSubscribers();
    fetchPlans();
  }, [fetchSubscribers, fetchPlans]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleDocClick = (e) => {
      if (!e.target.closest(".export-dropdown-wrapper")) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener("click", handleDocClick);
    }
    return () => {
      document.removeEventListener("click", handleDocClick);
    };
  }, [showExportMenu]);

  // ================= EXPORT SUBSCRIBERS TO EXCEL =================
  const handleExportExcel = async (type = "all") => {
    try {
      setExportLoading(true);
      setExportTypeLoading(type);
      setShowExportMenu(false);

      let downloadUrl = `${EXPORT_SUBSCRIBERS_API}?type=${type}`;
      if (dateFilterPreset !== "all") {
        downloadUrl += `&period=${encodeURIComponent(dateFilterPreset)}`;
        if (dateFilterPreset === "custom") {
          if (customStartDate) downloadUrl += `&from_date=${encodeURIComponent(customStartDate)}`;
          if (customEndDate) downloadUrl += `&to_date=${encodeURIComponent(customEndDate)}`;
        }
      }
      if (statusFilter !== "all") {
        downloadUrl += `&status=${encodeURIComponent(statusFilter)}`;
      }
      if (activeFilter !== "all") {
        downloadUrl += `&active_filter=${encodeURIComponent(activeFilter)}`;
      }

      const res = await fetch(downloadUrl);
      if (!res.ok) {
        throw new Error(`Export failed with status: ${res.status}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const todayStr = new Date().toISOString().split("T")[0];
      let fileName = `Vaithikasri_All_Subscribers_${todayStr}.xlsx`;
      if (customStartDate && customEndDate) {
        fileName = `Vaithikasri_Subscribers_${customStartDate}_to_${customEndDate}.xlsx`;
      } else if (type === "active") fileName = `Vaithikasri_Active_Subscribers_${todayStr}.xlsx`;
      else if (type === "renewals") fileName = `Vaithikasri_Renewals_${todayStr}.xlsx`;
      else if (type === "filtered") fileName = `Vaithikasri_Filtered_Subscribers_${todayStr}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export Excel error:", err);
      // Fallback: direct window download
      let fallbackUrl = `${EXPORT_SUBSCRIBERS_API}?type=${type}`;
      if (dateFilterPreset !== "all") {
        fallbackUrl += `&period=${encodeURIComponent(dateFilterPreset)}`;
        if (dateFilterPreset === "custom") {
          if (customStartDate) fallbackUrl += `&from_date=${encodeURIComponent(customStartDate)}`;
          if (customEndDate) fallbackUrl += `&to_date=${encodeURIComponent(customEndDate)}`;
        }
      }
      if (statusFilter !== "all") {
        fallbackUrl += `&status=${encodeURIComponent(statusFilter)}`;
      }
      if (activeFilter !== "all") {
        fallbackUrl += `&active_filter=${encodeURIComponent(activeFilter)}`;
      }
      window.open(fallbackUrl, "_blank");
    } finally {
      setExportLoading(false);
      setExportTypeLoading("");
    }
  };

  // ================= MANUAL IN-PERSON SUBSCRIBER & RENEWAL STATES =================
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [manualTab, setManualTab] = useState("create"); // "create" | "renew"
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSuccessData, setManualSuccessData] = useState(null);

  const getDefaultStartMonthAndYear = () => {
    const now = new Date();
    let m = now.getMonth() + 1;
    let y = now.getFullYear();
    if (now.getDate() >= 15) {
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return { month: m, year: y };
  };

  const defaultStartObj = getDefaultStartMonthAndYear();

  const initialCreateForm = {
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "Tamil Nadu",
    pincode: "",
    country: "India",
    plan_id: "",
    duration_years: null,
    start_month: defaultStartObj.month,
    start_year: defaultStartObj.year,
    payment_mode: "Cash / Counter",
    amount: "",
    remarks_1: "In-Person Subscription",
    send_whatsapp: true,
  };
  const [createForm, setCreateForm] = useState(initialCreateForm);

  const initialRenewForm = {
    subscriber: null,
    plan_id: "",
    duration_years: null,
    start_month: defaultStartObj.month,
    start_year: defaultStartObj.year,
    payment_mode: "Cash / Counter",
    amount: "",
    remarks_1: "In-Person Renewal",
    send_whatsapp: true,
  };
  const [renewForm, setRenewForm] = useState(initialRenewForm);
  const [renewSearchQuery, setRenewSearchQuery] = useState("");

  const calculatePeriod = (startMonth, startYear, durationYears) => {
    if (!durationYears) {
      return {
        displayString: "Select Duration (1 Year or 2 Years) to preview issue period",
      };
    }
    const sM = parseInt(startMonth, 10) || (new Date().getMonth() + 1);
    const sY = parseInt(startYear, 10) || new Date().getFullYear();
    const totalMonths = Number(durationYears) === 2 ? 24 : 12;
    const addMonths = totalMonths - 1;
    const eM = ((sM - 1 + addMonths) % 12) + 1;
    const eY = sY + Math.floor((sM - 1 + addMonths) / 12);
    const startName = MONTH_OPTIONS.find((m) => m.value === sM)?.label || `Month ${sM}`;
    const endName = MONTH_OPTIONS.find((m) => m.value === eM)?.label || `Month ${eM}`;
    return {
      startMonth: sM,
      startYear: sY,
      endMonth: eM,
      endYear: eY,
      startLabel: `${startName} ${sY}`,
      endLabel: `${endName} ${eY}`,
      displayString: `${startName} ${sY} to ${endName} ${eY} (${totalMonths} Magazine Issues)`,
    };
  };

  const handleOpenManualModal = (tab = "create") => {
    setManualTab(tab);
    setManualSuccessData(null);
    if (tab === "create") {
      const defStart = getDefaultStartMonthAndYear();
      setCreateForm({
        ...initialCreateForm,
        plan_id: "",
        duration_years: null,
        amount: "",
        start_month: defStart.month,
        start_year: defStart.year,
      });
    } else if (tab === "renew") {
      setRenewForm({
        ...initialRenewForm,
        plan_id: "",
        duration_years: null,
        amount: "",
      });
      setRenewSearchQuery("");
    }
    setIsManualModalOpen(true);
  };

  const existingActiveSubscriberByPhone = useMemo(() => {
    const cleanP = (createForm.phone || "").replace(/[^\d]/g, "");
    if (cleanP.length < 10) return null;
    const last10 = cleanP.slice(-10);
    return subscriptions.find((s) => {
      const sPhone = (s.user_phone || s.phone || "").replace(/[^\d]/g, "");
      return sPhone && sPhone.slice(-10) === last10;
    });
  }, [createForm.phone, subscriptions]);

  const handleSelectSubscriberForRenew = (sub) => {
    const defStart = getDefaultStartMonthAndYear();
    let nextStartMonth = defStart.month;
    let nextStartYear = defStart.year;

    if (sub.end_date) {
      const d = new Date(sub.end_date);
      if (!isNaN(d.getTime())) {
        const expM = d.getMonth() + 1;
        const expY = d.getFullYear();
        const today = new Date();
        // If current subscription is still active and ends in future
        if (d >= today) {
          nextStartMonth = (expM % 12) + 1;
          nextStartYear = expM === 12 ? expY + 1 : expY;
        } else {
          // Already expired: default to next dispatchable issue based on 15th cutoff
          nextStartMonth = defStart.month;
          nextStartYear = defStart.year;
        }
      }
    }

    setRenewForm({
      subscriber: sub,
      plan_id: "",
      duration_years: null,
      start_month: nextStartMonth,
      start_year: nextStartYear,
      payment_mode: "Cash / Counter",
      amount: "",
      remarks_1: "In-Person Renewal",
      send_whatsapp: true,
    });
    setRenewSearchQuery("");
  };

  const handleOpenRenewForSub = (sub) => {
    setManualTab("renew");
    setManualSuccessData(null);
    handleSelectSubscriberForRenew(sub);
    setIsManualModalOpen(true);
  };

  const renewSearchResults = useMemo(() => {
    const q = (renewSearchQuery || "").trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return subscriptions
      .filter((sub) => {
        const name = (sub.user_name || "").toLowerCase();
        const phone = (sub.user_phone || sub.phone || "").toLowerCase();
        const loginId = (sub.login_id || "").toLowerCase();
        const email = (sub.user_email || "").toLowerCase();
        const r1 = (sub.remarks_1 || "").toLowerCase();
        const r2 = (sub.remarks_2 || "").toLowerCase();
        const payId = (sub.payment_id || "").toLowerCase();
        return (
          name.includes(q) ||
          phone.includes(q) ||
          loginId.includes(q) ||
          email.includes(q) ||
          r1.includes(q) ||
          r2.includes(q) ||
          payId.includes(q)
        );
      })
      .slice(0, 15);
  }, [subscriptions, renewSearchQuery]);

  const handleCreatePlanOrDurationChange = (newPlanId, newDuration, country = createForm.country) => {
    const pId = newPlanId !== undefined ? (newPlanId ? String(newPlanId) : "") : String(createForm.plan_id || "");
    const dur = newDuration !== undefined ? (newDuration ? Number(newDuration) : null) : (createForm.duration_years ? Number(createForm.duration_years) : null);
    const plan = plans.find((p) => String(p.id) === pId);
    let price = "";
    if (plan && dur) {
      const isAbroad = (country || "").toLowerCase() !== "india" || plan.plan_type === "abroad";
      if (dur === 2) {
        price = isAbroad ? (plan.abroad_annual_price || plan.annual_price || 0) : (plan.annual_price || 0);
      } else {
        price = isAbroad ? (plan.abroad_monthly_price || plan.monthly_price || 0) : (plan.monthly_price || 0);
      }
    }
    setCreateForm((prev) => ({
      ...prev,
      plan_id: pId,
      duration_years: dur,
      amount: price,
      country: country,
    }));
  };

  const handleRenewPlanOrDurationChange = (newPlanId, newDuration) => {
    const pId = newPlanId !== undefined ? (newPlanId ? String(newPlanId) : "") : String(renewForm.plan_id || "");
    const dur = newDuration !== undefined ? (newDuration ? Number(newDuration) : null) : (renewForm.duration_years ? Number(renewForm.duration_years) : null);
    const plan = plans.find((p) => String(p.id) === pId);
    let price = "";
    if (plan && dur) {
      const subCountry = renewForm.subscriber?.country || renewForm.subscriber?.user_country || "India";
      const isAbroad = subCountry.toLowerCase() !== "india" || plan.plan_type === "abroad";
      if (dur === 2) {
        price = isAbroad ? (plan.abroad_annual_price || plan.annual_price || 0) : (plan.annual_price || 0);
      } else {
        price = isAbroad ? (plan.abroad_monthly_price || plan.monthly_price || 0) : (plan.monthly_price || 0);
      }
    }
    setRenewForm((prev) => ({
      ...prev,
      plan_id: pId,
      duration_years: dur,
      amount: price,
    }));
  };

  const handleManualCreateSubmit = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      showPopup("Please enter subscriber full name", "error");
      return;
    }
    if (!createForm.phone.trim() || createForm.phone.trim().length < 10) {
      showPopup("Please enter a valid 10-digit phone number", "error");
      return;
    }
    if (!createForm.pincode.trim()) {
      showPopup("Please enter postal pincode", "error");
      return;
    }
    if (!createForm.plan_id) {
      showPopup("Please select a Magazine Plan", "error");
      return;
    }
    if (!createForm.duration_years || (createForm.duration_years !== 1 && createForm.duration_years !== 2)) {
      showPopup("Please select Duration Period (1 Year or 2 Years)", "error");
      return;
    }
    if (!createForm.amount || Number(createForm.amount) <= 0) {
      showPopup("Please enter or verify the amount collected", "error");
      return;
    }

    setManualLoading(true);
    try {
      const res = await fetch(`${SUBSCRIBERS_API}/manual_create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: createForm.name.trim(),
          phone: createForm.phone.trim(),
          email: createForm.email.trim(),
          address: createForm.address.trim(),
          city: createForm.city.trim(),
          state: createForm.state.trim(),
          pincode: createForm.pincode.trim(),
          country: createForm.country.trim() || "India",
          plan_id: createForm.plan_id,
          is_annual: createForm.duration_years === 2 ? 1 : 0,
          start_month: createForm.start_month,
          start_year: createForm.start_year,
          payment_mode: createForm.payment_mode,
          amount: createForm.amount,
          remarks_1: createForm.remarks_1.trim() || "In-Person Subscription",
          send_whatsapp: true,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup(data.message || "Subscriber created successfully!");
        setManualSuccessData({
          type: "create",
          ...data.subscriber,
        });
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to create subscriber", "error");
      }
    } catch (err) {
      console.error("Manual create error:", err);
      showPopup("Error: " + err.message, "error");
    } finally {
      setManualLoading(false);
    }
  };

  const handleManualRenewSubmit = async (e) => {
    e.preventDefault();
    if (!renewForm.subscriber) {
      showPopup("Please search and select a subscriber to renew", "error");
      return;
    }
    if (!renewForm.plan_id) {
      showPopup("Please select a Renewal Magazine Plan", "error");
      return;
    }
    if (!renewForm.duration_years || (renewForm.duration_years !== 1 && renewForm.duration_years !== 2)) {
      showPopup("Please select Renewal Duration Period (1 Year or 2 Years)", "error");
      return;
    }
    if (!renewForm.amount || Number(renewForm.amount) <= 0) {
      showPopup("Please enter or verify the amount collected", "error");
      return;
    }

    setManualLoading(true);
    try {
      const res = await fetch(`${SUBSCRIBERS_API}/manual_renew`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subscriber_id: renewForm.subscriber.id,
          user_id: renewForm.subscriber.user_id,
          login_id: renewForm.subscriber.login_id,
          plan_id: renewForm.plan_id,
          is_annual: renewForm.duration_years === 2 ? 1 : 0,
          start_month: renewForm.start_month,
          start_year: renewForm.start_year,
          payment_mode: renewForm.payment_mode,
          amount: renewForm.amount,
          remarks_1: renewForm.remarks_1.trim() || "In-Person Renewal",
          send_whatsapp: true,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup(data.message || "Subscriber renewed successfully!");
        setManualSuccessData({
          type: "renew",
          ...data.renewal,
        });
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to renew subscriber", "error");
      }
    } catch (err) {
      console.error("Manual renew error:", err);
      showPopup("Error: " + err.message, "error");
    } finally {
      setManualLoading(false);
    }
  };



  // ================= BULK IMPORT HANDLER =================
  const handleImportSubmit = async () => {
    if (!importFile) return;

    setImporting(true);
    setImportStatus(null);

    const formData = new FormData();
    formData.append("file", importFile);

    try {
      const res = await fetch(IMPORT_SUBSCRIBERS_API, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (data.status === "success") {
        setImportStatus({
          type: "success",
          message: data.message
        });
        setImportFile(null);
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = "";
        showPopup("Subscribers imported successfully!");
        fetchSubscribers();
      } else if (data.status === "warning") {
        setImportStatus({
          type: "warning",
          message: data.message,
          errors: data.errors
        });
        fetchSubscribers();
      } else {
        setImportStatus({
          type: "error",
          message: data.message || "Import failed"
        });
      }
    } catch (err) {
      console.error(err);
      setImportStatus({
        type: "error",
        message: "Failed to upload file. Check server connection."
      });
    } finally {
      setImporting(false);
    }
  };

  // ================= BROADCAST NOTIFICATIONS HANDLER =================
  const handleBroadcastSubmit = async (e) => {
    e.preventDefault();
    if (!broadcastSubject || !broadcastMessage) {
      showPopup("Fill all broadcast fields", "error");
      return;
    }

    setBroadcasting(true);
    setBroadcastStatus(null);

    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "send_custom_notification",
          targetCategory: broadcastCategory,
          subject: broadcastSubject,
          message: broadcastMessage,
        }),
      });

      const data = await res.json();
      if (data.status === "success") {
        setBroadcastStatus({
          type: "success",
          message: data.message || "Notification broadcast sent successfully!",
        });
        showPopup("Broadcast sent successfully!");
        setBroadcastSubject("");
        setBroadcastMessage("");
      } else {
        setBroadcastStatus({
          type: "error",
          message: data.message || "Failed to broadcast notification.",
        });
        showPopup("Failed to send broadcast", "error");
      }
    } catch (err) {
      console.error(err);
      setBroadcastStatus({
        type: "error",
        message: "Server error occurred while sending broadcast.",
      });
      showPopup("Broadcast error", "error");
    } finally {
      setBroadcasting(false);
    }
  };

  // ================= SEND BULK WARNING ALERTS =================
  const handleBulkWarningSubmit = async (e) => {
    e.preventDefault();
    if (!bulkWarningType) {
      showPopup("Select a notice period", "error");
      return;
    }
    const ok = await showConfirm({
      title: "Send Bulk Warning",
      message: `Send a "${bulkWarningType}" warning to all subscribers in the selected category?`,
      subMessage: `Category: ${bulkWarningCategory.toUpperCase()}. This will notify all relevant subscribers.`,
      confirmText: "Send Warnings",
      type: "warning"
    });
    if (!ok) {
      return;
    }

    setBulkWarningSending(true);
    setBulkWarningStatus(null);

    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "send_bulk_warning",
          targetCategory: bulkWarningCategory,
          warningType: bulkWarningType,
        }),
      });

      const data = await res.json();
      if (data.status === "success") {
        setBulkWarningStatus({
          type: "success",
          message: data.message || "Bulk warnings sent successfully!",
        });
        showPopup("Bulk warnings pushed!");
        fetchSubscribers();
      } else {
        setBulkWarningStatus({
          type: "error",
          message: data.message || "Failed to send bulk warnings.",
        });
        showPopup("Failed to send bulk warnings", "error");
      }
    } catch (err) {
      console.error(err);
      setBulkWarningStatus({
        type: "error",
        message: "Server error occurred while pushing warnings.",
      });
      showPopup("Bulk warning error", "error");
    } finally {
      setBulkWarningSending(false);
    }
  };

  // ================= SEND WARNING ACTION =================
  const handleSendWarning = async (subId, type) => {
    const ok = await showConfirm({
      title: "Send Renewal Warning",
      message: `Send a "${type}" renewal warning to this subscriber?`,
      subMessage: "An alert notification will be sent to the subscriber.",
      confirmText: "Send Warning",
      type: "warning"
    });
    if (!ok) {
      return;
    }
    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "send_warning",
          subscription_id: subId,
          warning_type: type,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Warning notification sent successfully!");
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to send warning", "error");
      }
    } catch (err) {
      showPopup("Error sending warning", "error");
    }
  };

  // ================= SEND WHATSAPP CONFIRMATION =================
  const handleSendWhatsAppConfirmation = async (sub) => {
    const targetPhone = sub.user_phone || sub.phone;
    if (!targetPhone) {
      showPopup("No phone number found for this subscriber", "error");
      return;
    }

    const subName = sub.user_name || sub.name || "Subscriber";
    const subIdentifier = sub.login_id || `ID: ${sub.id}`;

    const ok = await showConfirm({
      title: "Send WhatsApp Confirmation",
      message: `Send official WhatsApp confirmation template via Interakt to ${subName}?`,
      subMessage: `Recipient Phone: +91 ${targetPhone} • Subscriber ID: ${subIdentifier}`,
      confirmText: "Send WhatsApp",
      type: "primary"
    });

    if (!ok) return;

    try {
      showPopup(`Sending WhatsApp notification to ${targetPhone}...`);
      const res = await fetch(`${WHATSAPP_API}/send_subscriber_notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscriber_id: sub.id })
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup(`WhatsApp confirmation queued successfully to ${targetPhone}!`);
      } else {
        showPopup(data.message || "Failed to send WhatsApp message", "error");
      }
    } catch (err) {
      console.error("WhatsApp send error:", err);
      showPopup("Network error: Failed to trigger WhatsApp message", "error");
    }
  };

  // ================= APPROVE RECEIPT ACTION =================
  const handleApproveReceipt = async (subId) => {
    const ok = await showConfirm({
      title: "Approve Subscription Receipt",
      message: "Approve this subscription receipt and send it to the subscriber?",
      subMessage: "The payment verification will be finalized and the receipt made available.",
      confirmText: "Approve Receipt",
      type: "success"
    });
    if (!ok) {
      return;
    }
    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "approve_receipt",
          subscription_id: subId,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Receipt approved and sent to subscriber!");
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to approve receipt", "error");
      }
    } catch (err) {
      showPopup("Error approving receipt", "error");
    }
  };

  // ================= EDIT SUBSCRIBER =================
  const handleEditClick = (sub) => {
    setEditingSub(sub);
    
    // Always use the exact term calculated for the table display to keep them 100% synchronized
    const term = getSubscriberTermDates(sub, false);
    const parsedStart = parseMonthYearFromValue(term.startStr !== "N/A" ? term.startStr : (sub.start_month || sub.start_date));
    let parsedEnd = parseMonthYearFromValue(term.endStr !== "N/A" ? term.endStr : (sub.end_month || sub.end_date));

    // Determine plan duration in months (12 or 24 or 48)
    const is4Y = (sub.package_name || "").toUpperCase().includes("4 YEAR") || Number(sub.amount) === 2400;
    const is2Y = !is4Y && ((sub.package_name || "").toUpperCase().includes("2 YEAR") || Number(sub.is_annual) === 1 || Number(sub.amount) >= 1200 || sub.plan_id === "2");
    const termMonths = is4Y ? 48 : (is2Y ? 24 : 12);
    const addMonths = termMonths - 1;

    // Safety guard: Magazine validity period must NEVER have same start and end month (13 or 25 issues)
    if (parsedStart.month === parsedEnd.month) {
      const sM = parsedStart.month;
      const sY = parsedStart.year;
      parsedEnd = {
        month: ((sM - 1 + addMonths) % 12) + 1,
        year: sY + Math.floor((sM - 1 + addMonths) / 12)
      };
    }

    setEditForm({
      subscription_id: sub.id,
      login_id: sub.login_id || "",
      user_name: sub.user_name || sub.name || "",
      user_email: sub.user_email || sub.email || "",
      user_phone: sub.user_phone || sub.phone || "",
      landline: sub.landline || "",
      address: sub.address || "",
      city: sub.city || "",
      state: sub.state || "",
      pincode: sub.pincode || "",
      amount: sub.amount || "",
      start_month: parsedStart.month,
      start_year: parsedStart.year,
      end_month: parsedEnd.month,
      end_year: parsedEnd.year,
      start_date: sub.start_date || "",
      end_date: sub.end_date || "",
      status: sub.status || "created",
      plan_id: sub.plan_id || "",
      order_id: sub.order_id || "",
      remarks_1: sub.remarks_1 || "",
      remarks_2: sub.remarks_2 || "",
      gift_by_sub_id: sub.gift_by_sub_id || "",
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const sM = parseInt(editForm.start_month, 10);
      const sY = parseInt(editForm.start_year, 10);
      const eM = parseInt(editForm.end_month, 10);
      const eY = parseInt(editForm.end_year, 10);

      const sMonthName = MONTH_OPTIONS.find(m => m.value === sM)?.label || `Month ${sM}`;
      const eMonthName = MONTH_OPTIONS.find(m => m.value === eM)?.label || `Month ${eM}`;

      const lastDayOfEndMonth = new Date(eY, eM, 0).getDate();

      const start_date = `${sY}-${String(sM).padStart(2, '0')}-01 00:00:00`;
      const end_date = `${eY}-${String(eM).padStart(2, '0')}-${String(lastDayOfEndMonth).padStart(2, '0')} 23:59:59`;
      const start_month = `${sMonthName} ${sY}`;
      const end_month = `${eMonthName} ${eY}`;

      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "edit_subscription",
          ...editForm,
          start_date,
          end_date,
          start_month,
          end_month,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Subscription updated successfully!");
        setEditingSub(null);
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to update subscription", "error");
      }
    } catch (err) {
      showPopup("Error: " + err.message, "error");
    }
  };

  // ================= DELETE SUBSCRIBER =================
  const handleDeleteSub = async (subId) => {
    const ok = await showConfirm({
      title: "Delete Subscriber Record",
      message: "Are you sure you want to delete this subscriber record?",
      subMessage: "This will permanently remove the subscriber entry.",
      confirmText: "Delete Record",
      type: "danger"
    });
    if (!ok) return;
    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "delete_subscription",
          subscription_id: subId
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Subscriber record deleted successfully!");
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to delete subscription", "error");
      }
    } catch (err) {
      showPopup("Error: " + err.message, "error");
    }
  };

  // ================= TOGGLE PAUSE =================
  const handleTogglePause = async (sub, actionType) => {
    const isPause = actionType === "pause";
    const ok = await showConfirm({
      title: isPause ? "Pause Subscription" : "Resume Subscription",
      message: isPause
        ? "Are you sure you want to pause receiving magazines for this subscriber temporarily?"
        : "Are you sure you want to resume receiving magazines for this subscriber?",
      subMessage: isPause ? "Dispatching will be temporarily halted until resumed." : "Magazine shipments will resume as normal.",
      confirmText: isPause ? "Pause Subscription" : "Resume Subscription",
      type: isPause ? "warning" : "info"
    });
    if (!ok) return;

    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: isPause ? "pause_subscription" : "resume_subscription",
          user_id: sub.user_id,
          subscription_id: sub.id,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup(isPause ? "Subscription paused temporarily" : "Subscription resumed");
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to update subscription status", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error: " + err.message, "error");
    }
  };

  // ================= CANCEL SUBSCRIPTION =================
  const handleCancelSubAdmin = async (sub) => {
    const ok = await showConfirm({
      title: "Cancel Subscription",
      message: "Are you sure you want to completely CANCEL this subscription?",
      subMessage: "The user's membership will be deactivated and magazine dispatches stopped.",
      confirmText: "Cancel Membership",
      type: "danger"
    });
    if (!ok) {
      return;
    }

    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "cancel_subscription",
          user_id: sub.user_id,
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        showPopup("Subscription cancelled successfully!");
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to cancel subscription", "error");
      }
    } catch (err) {
      console.error(err);
      showPopup("Server error: " + err.message, "error");
    }
  };

  const handleDismissAlert = async (subId) => {
    try {
      const res = await fetch(PAYMENT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "dismiss_admin_notification",
          subscription_id: subId
        }),
      });
      const data = await res.json();
      if (data.status === "success") {
        fetchSubscribers();
      } else {
        showPopup(data.message || "Failed to dismiss notification", "error");
      }
    } catch (err) {
      showPopup("Error: " + err.message, "error");
    }
  };

  const isAbroadSub = (sub) => {
    if (!sub) return false;
    if (Number(sub.is_abroad) === 1) return true;
    const c = (sub.country || sub.user_country || "").toLowerCase().trim();
    if (c && c !== "india") return true;
    const r1 = (sub.remarks_1 || "").toLowerCase();
    const r2 = (sub.remarks_2 || "").toLowerCase();
    const pkg = (sub.package_name || "").toLowerCase();
    if (r1.includes("abroad") || r2.includes("abroad") || pkg.includes("abroad")) return true;
    return false;
  };

  const getSubJoinYear = (sub) => {
    if (!sub) return null;
    const dateStr = sub.first_joined_date || sub.start_date || sub.created_at;
    if (!dateStr) return null;
    const match = String(dateStr).match(/^(\d{4})/);
    if (match) {
      return Number(match[1]);
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.getFullYear();
    return null;
  };

  const availableYears = useMemo(() => {
    const yearsSet = new Set();
    const currentYr = new Date().getFullYear();
    yearsSet.add(currentYr);
    subscriptions.forEach((sub) => {
      const yr = getSubJoinYear(sub);
      if (yr) yearsSet.add(yr);
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [subscriptions]);

  const yearCountsMap = useMemo(() => {
    const map = {};
    subscriptions.forEach((sub) => {
      const yr = getSubJoinYear(sub);
      if (yr) {
        map[yr] = (map[yr] || 0) + 1;
      }
    });
    return map;
  }, [subscriptions]);

  const yearJoinedCount = useMemo(() => {
    const yrNum = Number(selectedMetricYear);
    return subscriptions.filter((sub) => getSubJoinYear(sub) === yrNum).length;
  }, [subscriptions, selectedMetricYear]);


  // Filter logic
  const filteredSubs = useMemo(() => {
    const list = subscriptions.filter((sub) => {
      const searchText = (search || "").trim().toLowerCase();

      const matchSearch =
        !searchText ||
        (sub.user_name || "").toLowerCase().includes(searchText) ||
        (sub.user_email || "").toLowerCase().includes(searchText) ||
        (sub.user_phone || "").toLowerCase().includes(searchText) ||
        (sub.login_id || "").toLowerCase().includes(searchText) ||
        (sub.remarks_1 || "").toLowerCase().includes(searchText) ||
        (sub.remarks_2 || "").toLowerCase().includes(searchText) ||
        (sub.payment_id || "").toLowerCase().includes(searchText) ||
        (sub.order_id || "").toLowerCase().includes(searchText) ||
        (sub.gift_by_sub_id || "").toLowerCase().includes(searchText) ||
        (sub.pod_number || "").toLowerCase().includes(searchText);

      const statusLower = (sub.status || "").toLowerCase();
      let mappedStatus = "cancelled";
      if (statusLower === "paid") {
        mappedStatus = "paid";
      } else if (statusLower === "pending") {
        mappedStatus = "pending";
      } else if (statusLower === "paused") {
        mappedStatus = "paused";
      } else if (statusLower === "expired") {
        mappedStatus = "expired";
      }

      const matchStatus =
        statusFilter === "all" ||
        mappedStatus === statusFilter.toLowerCase();

      // Active filter logic
      let matchActive = true;
      if (activeFilter !== "all") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endDate = sub.end_date ? new Date(sub.end_date) : null;
        if (endDate) endDate.setHours(0, 0, 0, 0);

        if (activeFilter === "renewals") {
          matchActive = Number(sub.is_renewal) === 1 || sub.last_renewed_at != null;
        } else if (activeFilter === "pending") {
          matchActive = (sub.status || "").toLowerCase() === "pending" || Number(sub.receipt_approved) !== 1;
        } else if (activeFilter === "active") {
          // Subscription end date is today or in the future
          matchActive = endDate && endDate >= today;
        } else if (activeFilter === "expiring") {
          // Expiring within next 30 days
          const in30 = new Date(today);
          in30.setDate(in30.getDate() + 30);
          matchActive = endDate && endDate >= today && endDate <= in30;
        } else if (activeFilter === "expired_sub") {
          // End date has passed
          matchActive = !endDate || endDate < today;
        } else if (activeFilter === "abroad") {
          matchActive = isAbroadSub(sub);
        } else if (activeFilter === "local") {
          matchActive = !isAbroadSub(sub);
        }
      }

      // Joining year filter logic
      let matchYear = true;
      if (selectedYearFilter !== "all") {
        matchYear = getSubJoinYear(sub) === Number(selectedYearFilter);
      }

      // Date filter logic (Renewal date if viewing renewals, otherwise registration / join date)
      const dateToCheck = activeFilter === "renewals"
        ? (sub.last_renewed_at || sub.receipt_date)
        : (sub.created_at || sub.first_joined_date || sub.start_date);
      const matchDate = matchesDateFilter(dateToCheck, dateFilterPreset, customStartDate, customEndDate);

      return matchSearch && matchStatus && matchActive && matchYear && matchDate;
    });

    // When viewing Renewals, prioritize sorting by newest renewal date first so recent renewals show at the very top
    if (activeFilter === "renewals") {
      list.sort((a, b) => {
        const dateA = a.last_renewed_at || a.receipt_date;
        const dateB = b.last_renewed_at || b.receipt_date;
        const timeA = dateA ? new Date(dateA).getTime() : 0;
        const timeB = dateB ? new Date(dateB).getTime() : 0;
        return timeB - timeA;
      });
    }

    return list;
  }, [subscriptions, search, statusFilter, activeFilter, selectedYearFilter, dateFilterPreset, customStartDate, customEndDate]);

  // Pagination logic
  const indexOfLastSub = currentPage * subsPerPage;
  const indexOfFirstSub = indexOfLastSub - subsPerPage;
  const currentSubs = filteredSubs.slice(indexOfFirstSub, indexOfLastSub);
  const totalPages = Math.ceil(filteredSubs.length / subsPerPage);

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, activeFilter, selectedYearFilter, dateFilterPreset, customStartDate, customEndDate]);


  const getRemainingBanner = () => {
    if (editForm.status === "paused") {
      return (
        <div className="remaining-banner paused">
          <i className="bi bi-pause-circle-fill"></i>
          <span>Subscription is currently paused (temporarily cancelled).</span>
        </div>
      );
    }
    if (editForm.status === "cancelled") {
      return (
        <div className="remaining-banner expired">
          <i className="bi bi-x-circle-fill"></i>
          <span>Subscription is cancelled.</span>
        </div>
      );
    }
    if (!editForm.end_date) {
      return (
        <div className="remaining-banner paused">
          <i className="bi bi-question-circle-fill"></i>
          <span>No end date set for this subscription.</span>
        </div>
      );
    }
    
    const end = new Date(editForm.end_date);
    const today = new Date();
    // Reset hours to compare dates only
    end.setHours(0,0,0,0);
    today.setHours(0,0,0,0);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return (
        <div className="remaining-banner active">
          <i className="bi bi-check-circle-fill"></i>
          <span>Active: {diffDays} days remaining (ends in {formatSubEndMonth(editForm.start_date, editForm.end_date, editForm.is_annual)})</span>
        </div>
      );
    } else {
      return (
        <div className="remaining-banner expired">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <span>Expired: Subscription ended {Math.abs(diffDays)} days ago (ended in {formatSubEndMonth(editForm.start_date, editForm.end_date, editForm.is_annual)})</span>
        </div>
      );
    }
  };

  // Estimate Notified Count helper
  const getCategoryCount = (category) => {
    if (category === "all") return subscriptions.length;
    if (category === "paid") return subscriptions.filter(s => s.status === "paid" && s.is_active === 1).length;
    if (category === "expired") return subscriptions.filter(s => s.status === "expired").length;
    if (category.startsWith("plan_")) {
      const planId = category.replace("plan_", "");
      return subscriptions.filter(s => String(s.plan_id) === String(planId)).length;
    }
    return 0;
  };

  // Pending delivery notifications
  const pendingAlerts = subscriptions.filter((sub) => {
    if (sub.status === "paused" && sub.admin_notified === 0) return true;
    if (sub.status === "paid" && sub.admin_notified === 0 && sub.resumed_at) return true;
    return false;
  });

  /* ================= UI ================= */
  if (loading) {
    return (
      <div className="subscribers-loading-box">
        <div className="spinner"></div>
        <p>Loading subscribers database...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container subscribers-admin-container">
        <div className="subscribers-error-box" style={{ maxWidth: "600px", margin: "80px auto", padding: "36px 24px", background: "#ffffff", borderRadius: "16px", border: "1px solid #ebdcb9", textAlign: "center", boxShadow: "0 8px 30px rgba(93, 29, 43, 0.08)" }}>
          <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: "40px", color: "#d97706", display: "inline-block", marginBottom: "16px" }}></i>
          <h3 style={{ margin: "0 0 10px 0", color: "#4a1216", fontWeight: "750", fontSize: "1.25rem" }}>Unable to Load Subscribers</h3>
          <p style={{ color: "#64748b", fontSize: "14px", lineHeight: "1.6", margin: "0 0 24px 0" }}>{error}</p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
            <button
              type="button"
              onClick={() => fetchSubscribers()}
              style={{
                padding: "10px 22px",
                background: "linear-gradient(135deg, #4a1216 0%, #6b1d24 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "13.5px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 4px 12px rgba(74, 18, 22, 0.25)"
              }}
            >
              <i className="bi bi-arrow-clockwise"></i> Retry Loading Subscribers
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 18px",
                background: "#f1f5f9",
                color: "#334155",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "13.5px",
                cursor: "pointer"
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container subscribers-admin-container">
      {/* Toast Notification */}
      {popup && (
        <div className={`subscriber-toast ${popup.type}`}>
          {popup.type === "success" ? "✔" : "❌"} {popup.msg}
        </div>
      )}

      {/* ================= HEADER ================= */}
      <div className="subscribers-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-person-badge-fill"></i> Admin - Subscriber Management
          </h1>
          <p>Monitor, search, and manage registered spiritual magazine subscriptions</p>
        </div>

        {/* Dashboard Quick Stats */}
        <div className="subscribers-metrics-grid">
          <div className="metric-card">
            <div className="metric-icon total"><i className="bi bi-people-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{new Set(subscriptions.map(s => s.login_id || s.user_id)).size}</span>
              <span className="metric-label">Total Subscribers</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon paid"><i className="bi bi-check-circle-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">
                {subscriptions.filter((s) => s.status === "paid" && s.is_active === 1).length}
              </span>
              <span className="metric-label">Active Paid</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon paused"><i className="bi bi-pause-circle-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">
                {subscriptions.filter((s) => s.status === "paused").length}
              </span>
              <span className="metric-label">Paused</span>
            </div>
          </div>
          <div className="metric-card metric-card-yearly">
            <div className="metric-icon yearly"><i className="bi bi-calendar-check-fill"></i></div>
            <div className="metric-info">
              <div className="metric-value-row">
                <span className="metric-value">{yearJoinedCount}</span>
                <div className="metric-year-badge-wrapper">
                  <select
                    className="metric-year-pill-select"
                    value={selectedMetricYear}
                    onChange={(e) => {
                      const yr = Number(e.target.value);
                      setSelectedMetricYear(yr);
                    }}
                    title="Select Year"
                  >
                    {availableYears.map((yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ))}

                  </select>
                  <i className="bi bi-chevron-down pill-chevron"></i>
                </div>
              </div>
              <span className="metric-label">{selectedMetricYear} JOINED COUNT</span>
            </div>
          </div>
        </div>
      </div>


      {/* ================= ALERTS ================= */}
      {pendingAlerts.length > 0 && (
        <div className="delivery-alerts-box">
          <h4>
            <i className="bi bi-bell-fill"></i> Subscription Delivery Notifications
          </h4>
          <ul>
            {pendingAlerts.map((sub) => {
              const dateStr = sub.status === "paused" 
                ? (sub.temp_cancelled_at ? new Date(sub.temp_cancelled_at).toLocaleString() : "N/A")
                : (sub.resumed_at ? new Date(sub.resumed_at).toLocaleString() : "N/A");
              
              return (
                <li key={sub.id}>
                  <span>
                    Subscriber <strong>{sub.login_id || "N/A"}</strong> ({sub.user_name || "N/A"}) 
                    {sub.status === "paused" ? (
                      <span className="alert-badge paused"> paused book delivery (Temporarily Cancelled)</span>
                    ) : (
                      <span className="alert-badge resumed"> continued/resumed subscription book delivery</span>
                    )}{" "}
                    on {dateStr}.
                  </span>
                  <button 
                    type="button"
                    onClick={() => handleDismissAlert(sub.id)}
                    className="alert-ack-btn"
                  >
                    Acknowledge
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ================= NAVIGATION TABS & ACTION BUTTON ================= */}
      <div className="subscribers-tabs-header-row">
        <div className="subscribers-tabs">
          <button
            type="button"
            onClick={() => setActiveTab("directory")}
            className={`subscribers-tab-btn ${activeTab === "directory" ? "active" : ""}`}
          >
            <i className="bi bi-journal-text"></i>
            <div className="tab-text-container">
              <strong>Subscribers Directory</strong>
              <span className="tab-subtext">View and edit records</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("broadcast")}
            className={`subscribers-tab-btn ${activeTab === "broadcast" ? "active" : ""}`}
          >
            <i className="bi bi-megaphone-fill"></i>
            <div className="tab-text-container">
              <strong>Broadcast Alerts</strong>
              <span className="tab-subtext">Message by category</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("bulk-import")}
            className={`subscribers-tab-btn ${activeTab === "bulk-import" ? "active" : ""}`}
          >
            <i className="bi bi-file-earmark-arrow-up"></i>
            <div className="tab-text-container">
              <strong>Bulk Import Sheet</strong>
              <span className="tab-subtext">Upload excel templates</span>
            </div>
          </button>
        </div>

        <div className="manual-header-btn-group">
          {/* EXPORT EXCEL DROPDOWN */}
          <div className="export-dropdown-wrapper">
            <button
              type="button"
              className="btn-export-excel-trigger"
              onClick={() => setShowExportMenu((prev) => !prev)}
              disabled={exportLoading}
              title="Export subscribers list to Excel (.xlsx)"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm"></span>
                  <span>Exporting ({exportTypeLoading === "active" ? "Active" : "All"})...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-excel-fill" style={{ color: "#4ade80" }}></i>
                  <span>Export Excel</span>
                  <i className={`bi bi-chevron-${showExportMenu ? "up" : "down"}`} style={{ fontSize: "11px" }}></i>
                </>
              )}
            </button>

            {showExportMenu && (
              <div className="export-dropdown-menu">
                <div className="export-menu-header">
                  <i className="bi bi-file-earmark-spreadsheet-fill me-1" style={{ color: "#16a34a" }}></i> Choose Export Scope
                </div>

                {(dateFilterPreset !== "all" || customStartDate || customEndDate || search || statusFilter !== "all" || activeFilter !== "all" || selectedYearFilter !== "all") && (
                  <button
                    type="button"
                    className="export-dropdown-item"
                    onClick={() => handleExportExcel("filtered")}
                    style={{ background: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}
                  >
                    <div className="export-item-icon" style={{ background: "#dcfce7", color: "#15803d" }}>
                      <i className="bi bi-funnel-fill"></i>
                    </div>
                    <div className="export-item-content">
                      <strong style={{ color: "#166534" }}>Current Filtered View</strong>
                      <span className="export-item-sub">
                        {filteredSubs.length.toLocaleString("en-IN")} records {dateFilterPreset === "custom" && customStartDate ? `(${customStartDate} to ${customEndDate || "now"})` : "matching active filters"}
                      </span>
                    </div>
                  </button>
                )}

                <button
                  type="button"
                  className="export-dropdown-item"
                  onClick={() => handleExportExcel("all")}
                >
                  <div className="export-item-icon all">
                    <i className="bi bi-people-fill"></i>
                  </div>
                  <div className="export-item-content">
                    <strong>Full Subscribers List</strong>
                    <span className="export-item-sub">All {subscriptions.length.toLocaleString("en-IN")} records (Numerical ID order)</span>
                  </div>
                </button>

                <button
                  type="button"
                  className="export-dropdown-item"
                  onClick={() => handleExportExcel("active")}
                >
                  <div className="export-item-icon active">
                    <i className="bi bi-check-circle-fill"></i>
                  </div>
                  <div className="export-item-content">
                    <strong>Active Subscribers Only</strong>
                    <span className="export-item-sub">
                      Only currently active ({subscriptions.filter(s => s.status === 'paid' && s.is_active === 1).length.toLocaleString("en-IN")} members)
                    </span>
                  </div>
                </button>

                <button
                  type="button"
                  className="export-dropdown-item"
                  onClick={() => handleExportExcel("renewals")}
                >
                  <div className="export-item-icon renewals" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                    <i className="bi bi-arrow-repeat"></i>
                  </div>
                  <div className="export-item-content">
                    <strong>Renewals List</strong>
                    <span className="export-item-sub">
                      Renewals with dates & history ({subscriptions.filter(s => Number(s.is_renewal) === 1 || s.last_renewed_at != null).length.toLocaleString("en-IN")} records)
                    </span>
                  </div>
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="btn-open-manual-subscriber"
            onClick={() => handleOpenManualModal("create")}
            title="Register New In-Person Subscriber"
          >
            <i className="bi bi-person-plus-fill"></i>
            <span>+ Add Subscriber</span>
          </button>

          <button
            type="button"
            className="btn-open-manual-renew"
            onClick={() => handleOpenManualModal("renew")}
            title="Renew Existing Subscriber"
          >
            <i className="bi bi-arrow-repeat"></i>
            <span>🔄 Renew Subscriber</span>
          </button>
        </div>
      </div>

      {/* ================= TAB 1: DIRECTORY ================= */}
      {activeTab === "directory" && (
        <div className="tab-content-wrapper">
          {/* SEARCH & FILTERS */}
          <div className="subscribers-filter-deck">
            <div className="search-box-wrapper">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="admin-search-input"
                style={{ paddingLeft: "48px" }}
                placeholder="Search by Name, Phone, ID, Cheque No, Remarks, UTR / Ref..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="filter-select-wrapper">
              <i className="bi bi-calendar3"></i>
              <select
                value={selectedYearFilter}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedYearFilter(val);
                  if (val !== "all") {
                    setSelectedMetricYear(Number(val));
                  }
                }}
              >
                <option value="all">All Joining Years ({subscriptions.length})</option>
                {availableYears.map((yr) => (
                  <option key={yr} value={yr}>
                    📅 Joined in {yr} ({yearCountsMap[yr] || 0})
                  </option>
                ))}

              </select>
            </div>

            <div className="filter-select-wrapper">
              <i className="bi bi-funnel-fill"></i>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Payment Statuses</option>
                <option value="pending">🟡 Pending Verification (UPI)</option>
                <option value="paid">✅ Paid / Active</option>
                <option value="paused">⏸️ Paused</option>
                <option value="expired">⌛ Expired</option>
                <option value="cancelled">❌ Cancelled</option>
              </select>
            </div>

            {/* UNIVERSAL DATE FILTER */}
            <div className="filter-select-wrapper">
              <i className="bi bi-calendar-event"></i>
              <select
                value={dateFilterPreset}
                onChange={(e) => setDateFilterPreset(e.target.value)}
                title={activeFilter === "renewals" ? "Filter by renewal date" : "Filter by registration/joining date"}
              >
                <option value="all">
                  {activeFilter === "renewals" ? "All Renewal Dates" : "All Dates (Any Time)"}
                </option>
                <option value="today">
                  ⚡ Today ({activeFilter === "renewals" ? "Today's Renewals" : "Today's Joins"})
                </option>
                <option value="yesterday">
                  ⏪ Yesterday
                </option>
                <option value="last_7_days">
                  📅 Last 7 Days
                </option>
                <option value="this_month">
                  🗓️ This Month
                </option>
                <option value="custom">
                  🔍 Custom Date Range...
                </option>
              </select>
            </div>

            {dateFilterPreset === "custom" && (
              <div className="custom-date-range-group" style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <div className="filter-date-wrapper">
                  <i className="bi bi-calendar"></i>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    placeholder="From Date"
                    title="From Date"
                  />
                </div>
                <span style={{ color: "#94a3b8", fontWeight: "bold" }}>to</span>
                <div className="filter-date-wrapper">
                  <i className="bi bi-calendar"></i>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    placeholder="To Date"
                    title="To Date"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ACTIVE FILTER CHIPS */}
          <div className="subs-filter-chips">
            {[
              { key: "all",         label: "All Subscribers",      icon: "bi-people-fill" },
              { key: "renewals",    label: "Renewals",             icon: "bi-arrow-repeat" },
              { key: "pending",     label: "Pending Approval",     icon: "bi-hourglass-split" },
              { key: "active",      label: "Active",               icon: "bi-check-circle-fill" },
              { key: "expiring",    label: "Expiring in 30 days",  icon: "bi-clock-fill" },
              { key: "expired_sub", label: "Expired",              icon: "bi-x-circle-fill" },
              { key: "abroad",      label: "Abroad",               icon: "bi-globe-americas" },
              { key: "local",       label: "Local (India)",        icon: "bi-house-fill" },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                className={`subs-chip-btn chip-${key} ${activeFilter === key ? "subs-chip-active" : ""}`}
                onClick={() => setActiveFilter(key)}
                style={activeFilter === key ? { color: "#ffffff" } : undefined}
              >
                <i className={`bi ${icon}`} style={activeFilter === key ? { color: "#ffffff" } : undefined}></i>
                <span style={activeFilter === key ? { color: "#ffffff" } : undefined}>{label}</span>
                {key !== "all" && (
                  <span className="subs-chip-count">
                    {key === "renewals" && subscriptions.filter(s => Number(s.is_renewal) === 1 || s.last_renewed_at != null).length}
                    {key === "pending" && subscriptions.filter(s => (s.status || "").toLowerCase() === "pending" || Number(s.receipt_approved) !== 1).length}
                    {key === "active" && subscriptions.filter(s => { const t = new Date(); t.setHours(0,0,0,0); const e = s.end_date ? new Date(s.end_date) : null; if(e) e.setHours(0,0,0,0); return e && e >= t; }).length}
                    {key === "expiring" && subscriptions.filter(s => { const t = new Date(); t.setHours(0,0,0,0); const in30 = new Date(t); in30.setDate(in30.getDate()+30); const e = s.end_date ? new Date(s.end_date) : null; if(e) e.setHours(0,0,0,0); return e && e >= t && e <= in30; }).length}
                    {key === "expired_sub" && subscriptions.filter(s => { const t = new Date(); t.setHours(0,0,0,0); const e = s.end_date ? new Date(s.end_date) : null; if(e) e.setHours(0,0,0,0); return !e || e < t; }).length}
                    {key === "abroad" && subscriptions.filter(isAbroadSub).length}
                    {key === "local" && subscriptions.filter(s => !isAbroadSub(s)).length}
                  </span>
                )}
              </button>
            ))}

            {(activeFilter !== "all" || statusFilter !== "all" || selectedYearFilter !== "all" || dateFilterPreset !== "all" || customStartDate || customEndDate || search) && (
              <button
                type="button"
                className="subs-chip-clear"
                onClick={() => {
                  setActiveFilter("all");
                  setStatusFilter("all");
                  setSelectedYearFilter("all");
                  setDateFilterPreset("all");
                  setCustomStartDate("");
                  setCustomEndDate("");
                  setSearch("");
                }}
              >
                <i className="bi bi-x-lg"></i> Clear Filters
              </button>
            )}
          </div>


          {/* TABLE CONTAINER */}
          <section className="subscribers-table-section">
            <div className="section-header-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
              <h2>
                {activeFilter === "renewals" ? "Renewals List" : activeFilter === "expiring" ? "Expiring in 30 Days" : "Existing Subscribers"} ({filteredSubs.length})
                {dateFilterPreset !== "all" && (
                  <span style={{ fontSize: "13px", fontWeight: "normal", color: "#64748b", marginLeft: "10px" }}>
                    Filtered by: {dateFilterPreset === "today" ? "Today" : dateFilterPreset === "yesterday" ? "Yesterday" : dateFilterPreset === "last_7_days" ? "Last 7 Days" : dateFilterPreset === "this_month" ? "This Month" : `${customStartDate || "Start"} to ${customEndDate || "End"}`}
                  </span>
                )}
              </h2>

              {activeFilter === "expiring" && (
                <div
                  style={{
                    background: "#f0fdf4",
                    color: "#166534",
                    border: "1px solid #bbf7d0",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    fontWeight: "600",
                    fontSize: "12px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                >
                  <i className="bi bi-clock-history" style={{ color: "#16a34a" }}></i>
                  Automated WhatsApp Expiry Reminders Active (Daily 10:00 AM IST)
                </div>
              )}
            </div>

            {filteredSubs.length === 0 ? (
              <div className="subscribers-empty-state">
                <i className="bi bi-people"></i>
                <h3>No Subscribers Found</h3>
                <p>Try altering your filters. Subscriber registration events will display here automatically.</p>
              </div>
            ) : (
              <>
                <div className="subscribers-table-wrapper">
                  <table className="subscribers-admin-table">
                    <thead>
                      <tr>
                        <th style={{ width: "40px", textAlign: "center" }}>#</th>
                        <th style={{ minWidth: "110px" }}>Sub ID</th>
                        <th style={{ minWidth: "220px" }}>Subscriber</th>
                        <th style={{ minWidth: "120px" }}>Phone</th>
                        <th style={{ background: "#fffdf9", color: "#5d1d2b", borderBottom: "2px solid #bc9341", minWidth: "150px" }}>🚚 Delivery & POD</th>
                        <th style={{ minWidth: "85px" }}>Amount</th>
                        <th style={{ minWidth: "130px" }}>Payment Status</th>
                        <th style={{ minWidth: "130px" }}>Start / End Date</th>
                        <th style={{ minWidth: "160px" }}>Receipt</th>
                        <th style={{ minWidth: "130px" }}>Notice Alert</th>
                        <th style={{ width: "110px", minWidth: "110px", textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSubs.map((sub, index) => {
                        const statusLower = (sub.status || "").toLowerCase();
                        let statusLabel = "Created";
                        let badgeClass = "pending";

                        if (statusLower === "paid") {
                          statusLabel = "Paid";
                          badgeClass = "paid";
                        } else if (statusLower === "pending") {
                          statusLabel = "Pending Verification";
                          badgeClass = "pending";
                        } else if (statusLower === "paused") {
                          statusLabel = "Paused";
                          badgeClass = "paused";
                        } else if (statusLower === "expired") {
                          statusLabel = "Expired";
                          badgeClass = "expired";
                        } else if (statusLower === "cancelled") {
                          statusLabel = "Cancelled";
                          badgeClass = "cancelled";
                        }

                        return (
                          <tr key={sub.id || index}>
                            <td>{indexOfFirstSub + index + 1}</td>
                            <td className="sub-id-td">
                              <button
                                type="button"
                                className="sub-id-link-btn"
                                onClick={() => setViewingSub(sub)}
                                title="Click to view full subscriber profile"
                              >
                                {sub.login_id || "N/A"}
                              </button>
                            </td>
                            <td>
                              <div className="subscriber-cell-name">
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                  <button
                                    type="button"
                                    className="sub-name-link-btn"
                                    onClick={() => setViewingSub(sub)}
                                    title="Click to view full subscriber profile"
                                  >
                                    {sub.user_name || "N/A"}
                                  </button>
                                  {sub.last_renewed_at ? (
                                    <span
                                      style={{ background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "800" }}
                                      title={`Last Renewed: ${new Date(sub.last_renewed_at).toLocaleString()}`}
                                    >
                                      🔄 Renewed: {new Date(sub.last_renewed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                    </span>
                                  ) : Number(sub.is_renewal) === 1 ? (
                                    <span style={{ background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "800" }}>
                                      🔄 Renewal
                                    </span>
                                  ) : null}
                                  {(Number(sub.is_vip) === 1 || Number(sub.user_is_vip) === 1) && (
                                    <span style={{ background: "#854d0e", color: "#fef08a", border: "1px solid #ca8a04", padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "800" }}>
                                      ⭐ VIP / Complimentary
                                    </span>
                                  )}
                                  {sub.gift_by_sub_id && (
                                    <span style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", padding: "1px 6px", borderRadius: "8px", fontSize: "10px", fontWeight: "700" }}>
                                      🎁 Ref: {sub.gift_by_sub_id}
                                    </span>
                                  )}
                                  {isAbroadSub(sub) && (
                                    <span style={{ background: "#0284c7", color: "#ffffff", padding: "2px 6px", borderRadius: "10px", fontSize: "10px", fontWeight: "800" }}>
                                      🌐 Abroad ({sub.country || sub.user_country || "Overseas"})
                                    </span>
                                  )}
                                </div>
                                <span className="subscriber-cell-email">{sub.user_email || "N/A"}</span>
                                {sub.remarks_1 && (
                                  <span style={{ fontSize: "10.5px", color: "#64748b", fontStyle: "italic" }}>
                                    📝 {sub.remarks_1}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                <span>{sub.user_phone || "N/A"}</span>
                                {sub.landline && (
                                  <span style={{ fontSize: "11px", color: "#64748b" }}>
                                    ☎️ {sub.landline}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="delivery-status-td" style={{ background: "#fffdf9", verticalAlign: "middle" }}>
                              {statusLower === "paid" && sub.is_active === 1 ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                                  <span className="delivery-status active" style={{ fontSize: "11px", fontWeight: "800", color: "#2e7d32" }}>✅ Send Book</span>
                                  {sub.pod_number ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                      <span style={{ background: "#2e7d32", color: "#ffffff", padding: "3px 8px", borderRadius: "4px", fontSize: "11.5px", fontWeight: "bold", fontFamily: "monospace", display: "inline-block" }}>
                                        🚚 {sub.pod_number}
                                      </span>
                                      <button
                                        type="button"
                                        style={{ background: "#5d1d2b", color: "#ffffff", border: "none", borderRadius: "4px", padding: "3px 8px", fontSize: "11px", fontWeight: "bold", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "3px" }}
                                        onClick={() => handleOpenPodModal(sub)}
                                        title="Edit POD Tracking Number"
                                      >
                                        <i className="bi bi-pencil-square"></i> Edit
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      style={{ background: "#5d1d2b", color: "#ffffff", border: "1.5px solid #5d1d2b", padding: "5px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", boxShadow: "0 2px 4px rgba(93, 29, 43, 0.2)" }}
                                      onClick={() => handleOpenPodModal(sub)}
                                    >
                                      + Add POD
                                    </button>
                                  )}
                                </div>
                              ) : statusLower === "paused" ? (
                                <span className="delivery-status paused">❌ Temp Cancelled</span>
                              ) : (
                                <span className="delivery-status blocked">❌ Do Not Send</span>
                              )}
                            </td>

                            <td className="sub-amount-td">{sub.amount ? `₹${sub.amount}` : "N/A"}</td>
                            <td>
                              <span className={`status-pill ${badgeClass}`}>
                                {statusLabel}
                              </span>
                              {sub.payment_mode && (
                                <span style={{ fontSize: "10.5px", color: "#5d1d2b", background: "#f5eee2", padding: "2px 6px", borderRadius: "4px", display: "block", marginTop: "4px", fontWeight: "700" }}>
                                  {sub.payment_mode.includes("QR") ? "📱 UPI QR Code" : sub.payment_mode}
                                </span>
                              )}
                              {sub.payment_proof && (
                                <a
                                  href={getImageUrl(sub.payment_proof)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "3px",
                                    fontSize: "10.5px",
                                    background: "#fdf8e2",
                                    border: "1px solid #d4af37",
                                    color: "#5d1d2b",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    marginTop: "4px",
                                    textDecoration: "none",
                                    fontWeight: "800",
                                  }}
                                  title="View Uploaded Payment Screenshot / Proof"
                                >
                                  <i className="bi bi-image"></i> View Proof
                                </a>
                              )}
                            </td>
                            <td className="sub-date-td" style={{ fontSize: "12px" }}>
                              {(() => {
                                const term = getSubscriberTermDates(sub);
                                return (
                                  <>
                                    <div><strong>Start:</strong> {term.startStr}</div>
                                    <div><strong>End:</strong> {term.endStr}</div>
                                    {term.memberSinceStr && (
                                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }} title="Subscriber's original lifetime joining date">
                                        🎖️ Joined: {term.memberSinceStr}
                                      </div>
                                    )}
                                    {sub.last_renewed_at && (
                                      <div style={{ color: "#0369a1", fontWeight: "700", marginTop: "3px", fontSize: "11px" }}>
                                        🔄 Renewed: {new Date(sub.last_renewed_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                            <td>
                              {statusLower === "pending" || Number(sub.receipt_approved) !== 1 ? (
                                <div className="receipt-pending-block">
                                  <span className="badge-receipt-pending">
                                    <span className="pending-pulse-dot"></span>
                                    {sub.payment_mode && sub.payment_mode.includes("Razorpay")
                                      ? "⚡ Payment Verified — Awaiting Activation"
                                      : "Pending Approval"
                                    }
                                  </span>
                                  <div className="receipt-quick-row">
                                    <button
                                      type="button"
                                      className="receipt-action-btn approve"
                                      onClick={() => handleApproveReceipt(sub.id)}
                                      title="Verify payment and approve subscription receipt"
                                    >
                                      <i className="bi bi-check2"></i> Approve
                                    </button>
                                  </div>
                                </div>
                              ) : statusLower === "paid" ? (
                                <button
                                  type="button"
                                  className="receipt-action-btn print"
                                  onClick={() => handleOpenReceiptForSub(sub)}
                                >
                                  <i className="bi bi-printer"></i> Print
                                </button>
                              ) : (
                                <span className="text-disabled">N/A</span>
                              )}
                            </td>
                            <td>
                              {statusLower === "paid" ? (
                                <div className="notice-picker-block">
                                  <select
                                    defaultValue=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        if (e.target.value === "send_whatsapp") {
                                          handleSendWhatsAppConfirmation(sub);
                                        } else {
                                          handleSendWarning(sub.id, e.target.value);
                                        }
                                        e.target.value = "";
                                      }
                                    }}
                                  >
                                    <option value="" disabled>Notice / Receipt</option>
                                    <option value="send_whatsapp">💬 Send Welcome / Receipt (WA)</option>
                                    <option value="1 Month Before">1 Month Before (Portal)</option>
                                    <option value="15 Days Before">15 Days Before (Portal)</option>
                                    <option value="1 Week Before">1 Week Before (Portal)</option>
                                  </select>
                                  {sub.last_whatsapp_reminder && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "4px", fontSize: "11px", color: "#16a34a", fontWeight: "600" }}>
                                      <i className="bi bi-whatsapp"></i> Reminded: {new Date(sub.last_whatsapp_reminder).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                                    </span>
                                  )}
                                  {sub.renewal_warning && (
                                    <span className={`warning-status ${sub.warning_dismissed ? "seen" : "active"}`}>
                                      {sub.renewal_warning} {sub.warning_dismissed ? "(Seen)" : "(Active)"}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-disabled">N/A</span>
                              )}
                            </td>
                            <td>
                              <div className="table-actions-row">
                                <button
                                  type="button"
                                  className="action-btn-view"
                                  onClick={() => setViewingSub(sub)}
                                  title="View Full Subscriber Profile"
                                >
                                  <i className="bi bi-eye-fill"></i> View
                                </button>
                                <button
                                  type="button"
                                  className="action-btn-history"
                                  onClick={() => handleOpenHistory(sub)}
                                  title="View Receipts History"
                                >
                                  <i className="bi bi-clock-history"></i> History
                                </button>
                                <button
                                  type="button"
                                  className="action-btn-renew"
                                  onClick={() => handleOpenRenewForSub(sub)}
                                  title="Renew Subscription"
                                >
                                  <i className="bi bi-arrow-repeat"></i> Renew
                                </button>
                                <button
                                  type="button"
                                  className="action-btn-edit"
                                  onClick={() => handleEditClick(sub)}
                                  title="Edit Subscriber"
                                >
                                  <i className="bi bi-pencil-fill"></i> Edit
                                </button>
                                <button
                                  type="button"
                                  className="action-btn-delete"
                                  onClick={() => handleDeleteSub(sub.id)}
                                  title="Delete Record"
                                >
                                  <i className="bi bi-trash-fill"></i> Delete
                                </button>
                                {sub.is_active === 1 && (
                                  <>
                                    {statusLower === "paid" ? (
                                      <button
                                        type="button"
                                        className="action-btn-pause"
                                        onClick={() => handleTogglePause(sub, "pause")}
                                        title="Pause Subscription"
                                      >
                                        <i className="bi bi-pause-fill"></i> Pause
                                      </button>
                                    ) : statusLower === "paused" ? (
                                      <button
                                        type="button"
                                        className="action-btn-resume"
                                        onClick={() => handleTogglePause(sub, "resume")}
                                        title="Resume Subscription"
                                      >
                                        <i className="bi bi-play-fill"></i> Resume
                                      </button>
                                    ) : null}
                                    {statusLower !== "cancelled" && (
                                      <button
                                        type="button"
                                        className="action-btn-cancel-sub"
                                        onClick={() => handleCancelSubAdmin(sub)}
                                        title="Cancel Subscription"
                                      >
                                        <i className="bi bi-x-circle-fill"></i> Cancel
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (() => {
                  const getPageWindow = () => {
                    const pages = [];
                    const delta = 2;
                    const left = Math.max(2, currentPage - delta);
                    const right = Math.min(totalPages - 1, currentPage + delta);
                    pages.push(1);
                    if (left > 2) pages.push("...");
                    for (let i = left; i <= right; i++) pages.push(i);
                    if (right < totalPages - 1) pages.push("...");
                    if (totalPages > 1) pages.push(totalPages);
                    return pages;
                  };

                  const firstItem = (currentPage - 1) * subsPerPage + 1;
                  const lastItem = Math.min(currentPage * subsPerPage, filteredSubs.length);

                  return (
                    <div className="subs-pagination-bar">
                      {/* Left: record range */}
                      <span className="subs-pg-info">
                        Showing <strong>{firstItem}–{lastItem}</strong> of <strong>{filteredSubs.length}</strong> subscribers
                      </span>

                      {/* Center: nav controls */}
                      <div className="subs-pg-controls">
                        <button
                          type="button"
                          onClick={() => goToPage(1)}
                          disabled={currentPage === 1}
                          className="subs-pg-prev-next"
                          title="First page"
                        >
                          <i className="bi bi-chevron-double-left"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => goToPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="subs-pg-prev-next"
                        >
                          <i className="bi bi-chevron-left"></i> Prev
                        </button>

                        <div className="subs-pg-btns">
                          {getPageWindow().map((page, idx) =>
                            page === "..." ? (
                              <span key={`ell-${idx}`} className="subs-pg-ellipsis">…</span>
                            ) : (
                              <button
                                key={page}
                                type="button"
                                onClick={() => goToPage(page)}
                                className={`subs-pg-num ${currentPage === page ? "subs-pg-active" : ""}`}
                              >
                                {page}
                              </button>
                            )
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => goToPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="subs-pg-prev-next"
                        >
                          Next <i className="bi bi-chevron-right"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => goToPage(totalPages)}
                          disabled={currentPage === totalPages}
                          className="subs-pg-prev-next"
                          title="Last page"
                        >
                          <i className="bi bi-chevron-double-right"></i>
                        </button>
                      </div>

                      {/* Right: page indicator */}
                      <span className="subs-pg-info" style={{ textAlign: "right" }}>
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                      </span>
                    </div>
                  );
                })()}
              </>
            )}
          </section>
        </div>
      )}

      {/* ================= TAB 2: BROADCAST ALERTS ================= */}
      {activeTab === "broadcast" && (
        <div className="tab-content-wrapper fade-in-tab">
          <div className="broadcast-tab-layout-grid">
            {/* Form Column */}
            <div className="form-column-container">
              {/* Form 1: Email Broadcast */}
              <div className="admin-section form-column" style={{ marginBottom: "30px" }}>
                <h2><i className="bi bi-envelope-fill"></i> Send Broadcast Email</h2>
                <p>Draft and broadcast custom email newsletters or announcement notifications directly to a filtered segment of your subscribers.</p>

                <form onSubmit={handleBroadcastSubmit} className="broadcast-form">
                  <div className="form-group">
                    <label className="field-label">Target Recipient Category</label>
                    <select
                      value={broadcastCategory}
                      onChange={(e) => setBroadcastCategory(e.target.value)}
                      className="broadcast-select"
                    >
                      <option value="all">📢 All Registered Subscribers</option>
                      <option value="paid">🟢 Active Paid Subscribers Only</option>
                      <option value="expired">🔴 Expired Subscribers Only</option>
                      <option value="" disabled>--- Filter By Specific Pricing Plan ---</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={`plan_${plan.id}`}>
                          📦 Plan: {plan.monthly_title} (ID: {plan.id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="field-label">Broadcast Email Subject</label>
                    <input
                      type="text"
                      placeholder="e.g. Important Magazine Renewal Notice or Welcome to Vaithikasri Digital"
                      value={broadcastSubject}
                      onChange={(e) => setBroadcastSubject(e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="field-label">Announcement Content (Plaintext / HTML support)</label>
                    <textarea
                      placeholder="Type your notification message here. Use shift+enter for new lines."
                      value={broadcastMessage}
                      onChange={(e) => setBroadcastMessage(e.target.value)}
                      rows={6}
                      required
                    />
                  </div>

                  {broadcastStatus && (
                    <div className={`broadcast-status-alert ${broadcastStatus.type}`}>
                      <i className={broadcastStatus.type === "success" ? "bi bi-check-circle-fill" : "bi bi-exclamation-circle-fill"}></i>
                      <span>{broadcastStatus.message}</span>
                      <button type="button" className="alert-dismiss-btn" onClick={() => setBroadcastStatus(null)} aria-label="Dismiss">&times;</button>
                    </div>
                  )}

                  <div className="broadcast-form-buttons">
                    <button
                      type="submit"
                      className="broadcast-submit-btn"
                      disabled={broadcasting}
                    >
                      {broadcasting ? (
                        <>
                          <span className="spinner-border-btn"></span> Sending Broadcast...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill"></i> Broadcast Notification
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Form 2: Bulk Notice Warnings */}
              <div className="admin-section form-column">
                <h2><i className="bi bi-bell-fill"></i> Send Bulk Notice Alerts</h2>
                <p>Push instant dashboard renewal notice alerts (e.g. 1 Month Before, 15 Days Before) to all subscribers belonging to a specific category or plan.</p>

                <form onSubmit={handleBulkWarningSubmit} className="broadcast-form">
                  <div className="form-group">
                    <label className="field-label">Target Recipient Category</label>
                    <select
                      value={bulkWarningCategory}
                      onChange={(e) => setBulkWarningCategory(e.target.value)}
                      className="broadcast-select"
                    >
                      <option value="all">📢 All Registered Subscribers</option>
                      <option value="paid">🟢 Active Paid Subscribers Only</option>
                      <option value="expired">🔴 Expired Subscribers Only</option>
                      <option value="" disabled>--- Filter By Specific Pricing Plan ---</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={`plan_${plan.id}`}>
                          📦 Plan: {plan.monthly_title} (ID: {plan.id})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="field-label">Notice Warning Alert Type</label>
                    <select
                      value={bulkWarningType}
                      onChange={(e) => setBulkWarningType(e.target.value)}
                      className="broadcast-select"
                    >
                      <option value="1 Month Before">⏳ 1 Month Before Expiry Notice</option>
                      <option value="15 Days Before">⏳ 15 Days Before Expiry Notice</option>
                      <option value="1 Week Before">⏳ 1 Week Before Expiry Notice</option>
                    </select>
                  </div>

                  {bulkWarningStatus && (
                    <div className={`broadcast-status-alert ${bulkWarningStatus.type}`}>
                      <i className={bulkWarningStatus.type === "success" ? "bi bi-check-circle-fill" : "bi bi-exclamation-circle-fill"}></i>
                      <span>{bulkWarningStatus.message}</span>
                      <button type="button" className="alert-dismiss-btn" onClick={() => setBulkWarningStatus(null)} aria-label="Dismiss">&times;</button>
                    </div>
                  )}

                  <div className="broadcast-form-buttons">
                    <button
                      type="submit"
                      className="broadcast-submit-btn"
                      style={{ background: "#bc9341" }}
                      disabled={bulkWarningSending}
                    >
                      {bulkWarningSending ? (
                        <>
                          <span className="spinner-border-btn"></span> Pushing Warnings...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-bell-fill"></i> Send Bulk Notice Alerts
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sidebar info */}
            <div className="admin-section info-column">
              <h2>Target Audience Details</h2>
              <p>Visual statistics for your current filters.</p>

              {/* Broadcast Target Count */}
              <div className="cohort-estimate-card" style={{ marginBottom: "20px" }}>
                <div className="cohort-count-circle">
                  <span>{getCategoryCount(broadcastCategory)}</span>
                </div>
                <p className="cohort-label">Email Broadcast Recipients</p>
                <div className="cohort-meta">
                  <div className="meta-row">
                    <span>Group:</span>
                    <strong>
                      {broadcastCategory === "all" && "All Subscribers"}
                      {broadcastCategory === "paid" && "Active Paid"}
                      {broadcastCategory === "expired" && "Expired"}
                      {broadcastCategory.startsWith("plan_") && `Plan ID: ${broadcastCategory.replace("plan_", "")}`}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Bulk Notice Target Count */}
              <div className="cohort-estimate-card" style={{ borderLeft: "4px solid #bc9341" }}>
                <div className="cohort-count-circle" style={{ borderColor: "rgba(188, 147, 65, 0.3)" }}>
                  <span style={{ color: "#bc9341" }}>{getCategoryCount(bulkWarningCategory)}</span>
                </div>
                <p className="cohort-label">Dashboard Warning Notice Recipients</p>
                <div className="cohort-meta">
                  <div className="meta-row">
                    <span>Group:</span>
                    <strong>
                      {bulkWarningCategory === "all" && "All Subscribers"}
                      {bulkWarningCategory === "paid" && "Active Paid"}
                      {bulkWarningCategory === "expired" && "Expired"}
                      {bulkWarningCategory.startsWith("plan_") && `Plan ID: ${bulkWarningCategory.replace("plan_", "")}`}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="broadcast-tips-box" style={{ marginTop: "20px" }}>
                <h4><i className="bi bi-lightbulb-fill"></i> Broadcast & Notice Tips</h4>
                <ul>
                  <li><strong>Email Broadcasts</strong> send HTML emails directly using nodemailer.</li>
                  <li><strong>Notice Alerts</strong> are instant system reminders that appear on the subscriber's front-end portal dashboard.</li>
                  <li>Notice alerts will display a warning until dismissed by the subscriber.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= TAB 3: BULK IMPORT ================= */}
      {activeTab === "bulk-import" && (
        <div className="tab-content-wrapper fade-in-tab">
          <div className="admin-section bulk-import-section">
            <h2>📥 Bulk Import Subscribers</h2>
            <p>Bulk import legacy subscribers using an Excel template. If <strong>Subscriber ID</strong> is left blank, the system automatically allocates sequential IDs (e.g. <code>VS18270</code>, <code>VS18271</code>...). Newly created subscriber accounts will automatically use their <strong>Phone Number</strong> as their temporary login password.</p>

            <div className="bulk-import-grid">
              {/* Card 1 */}
              <div className="import-step-card">
                <div className="step-info">
                  <h4>1. Download Excel Template</h4>
                  <p>Get the clean Excel template with the correct column structure ready for filling in subscriber details.</p>
                </div>
                <a
                  href={DOWNLOAD_TEMPLATE_API}
                  className="step-action-download"
                >
                  <i className="bi bi-file-earmark-excel-fill"></i> Download Template (Excel)
                </a>
              </div>

              {/* Card 2 */}
              <div className="import-step-card">
                <div className="step-info">
                  <h4>2. Upload & Import Sheet</h4>
                  <p>Select your completed Excel file to import your legacy subscribers into the platform.</p>
                </div>
                
                <div className="step-upload-controls">
                  <label className="file-picker-label">
                    <i className="bi bi-folder-fill"></i>
                    {importFile ? importFile.name : "Choose Excel File"}
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={(e) => setImportFile(e.target.files[0])}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={handleImportSubmit}
                    disabled={importing || !importFile}
                    className="import-submit-btn"
                  >
                    {importing ? "Importing..." : "Upload & Import"}
                  </button>
                </div>
              </div>
            </div>

            {importStatus && (
              <div className={`import-status-box ${importStatus.type}`} style={{ marginTop: "16px" }}>
                <strong>{importStatus.message}</strong>
                {importStatus.errors && importStatus.errors.length > 0 && (
                  <ul>
                    {importStatus.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Column Structure Specification Reference */}
            <div className="admin-section" style={{ marginTop: "24px", background: "#fcfbf7", border: "1px solid #ebdcb9", borderRadius: "12px", padding: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                <h4 style={{ color: "#5d1d2b", margin: 0, fontSize: "1rem", fontWeight: "750", display: "flex", alignItems: "center", gap: "8px" }}>
                  <i className="bi bi-table" style={{ color: "#bc9341" }}></i> 16-Column Template Specification & Rules
                </h4>
                <span style={{ fontSize: "12px", background: "#efe3c3", color: "#5d1d2b", padding: "3px 10px", borderRadius: "12px", fontWeight: "700" }}>
                  9 Mandatory • 7 Optional (Subscriber ID auto-generated if blank)
                </span>
              </div>
              <p style={{ fontSize: "13px", color: "#666", marginBottom: "16px" }}>
                Ensure your Excel sheet contains the headers below. Optional columns can be left blank without causing upload errors. 
                Dates accept standard months like <code style={{ color: "#5d1d2b", background: "#fff", padding: "2px 5px", borderRadius: "4px" }}>2026-04</code> or <code style={{ color: "#5d1d2b", background: "#fff", padding: "2px 5px", borderRadius: "4px" }}>April 2026</code>.
              </p>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: "12.5px", borderCollapse: "collapse", textAlign: "left" }}>
                  <thead>
                    <tr style={{ background: "#5d1d2b", color: "#ffffff" }}>
                      <th style={{ padding: "8px 12px", borderRadius: "6px 0 0 0" }}>#</th>
                      <th style={{ padding: "8px 12px" }}>Excel Header</th>
                      <th style={{ padding: "8px 12px" }}>Requirement</th>
                      <th style={{ padding: "8px 12px" }}>Example Value</th>
                      <th style={{ padding: "8px 12px", borderRadius: "0 6px 0 0" }}>Notes / Rules</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#fdfbf7" }}>
                      <td style={{ padding: "8px 12px" }}>1</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Subscriber ID</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional (Auto-Generated)</span></td>
                      <td style={{ padding: "8px 12px" }}><code>VS1001</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Leave blank to auto-generate (e.g. VS18270), or enter legacy ID</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>2</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Subscriber Name</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}>Chandramouli</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Full devotee name</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>3</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>Email</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}>chandramouli@example.com</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Auto-generated if empty</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>4</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Phone Number</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>9876543210</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>10-digit mobile (temp password)</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>5</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>Landline Number</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}><code>044-24474312</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Landline / STD phone number</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>6</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Address</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}>No.38 Krishnan Veethi, Gobichettipalayam</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Street, Door No & Locality</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>7</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>City</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}>Erode</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>City / Town</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>8</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>State</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}>Tamil Nadu</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>State or Province</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>9</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Pincode</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>638452</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>6-digit postal pin</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>10</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Plan ID</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>1</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>1: General Post, 2: Courier, 3: Speed Post, 6: Abroad</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>11</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Amount</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>600.00</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>600 (1 Yr) or 1200 (2 Yrs)</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>12</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>Start Month</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>2026-04</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Accepts <code>2026-04</code>, <code>April 2026</code>, <code>04/2026</code></td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9" }}>
                      <td style={{ padding: "8px 12px" }}>13</td>
                      <td style={{ padding: "8px 12px", fontWeight: "700" }}>End Month</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#b91c1c", fontWeight: "700" }}>● Mandatory</span></td>
                      <td style={{ padding: "8px 12px" }}><code>2027-03</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Accepts <code>2027-03</code>, <code>March 2027</code>, <code>03/2027</code></td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>14</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>Remark 1</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}>Annual Subscriber</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Dispatch note (e.g. Hand Delivery, Abroad)</td>
                    </tr>
                    <tr style={{ borderBottom: "1px solid #ebdcb9", background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>15</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>Remark 2</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}>General Post Dispatch</td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Office notes / special instructions</td>
                    </tr>
                    <tr style={{ background: "#f8fafc" }}>
                      <td style={{ padding: "8px 12px" }}>16</td>
                      <td style={{ padding: "8px 12px", fontWeight: "600" }}>Gifted/Referred By ID</td>
                      <td style={{ padding: "8px 12px" }}><span style={{ color: "#059669", fontWeight: "700" }}>○ Optional</span></td>
                      <td style={{ padding: "8px 12px" }}><code>VS1000</code></td>
                      <td style={{ padding: "8px 12px", color: "#666" }}>Donor or Referrer Sub ID / Name</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= VIEW SUBSCRIBER PROFILE DRAWER ================= */}
      {viewingSub && (() => {
        const r1 = (viewingSub.remarks_1 || "").trim();
        const r2 = (viewingSub.remarks_2 || "").trim();

        // Cheque / draft / payment instrument pattern matcher
        const chequeRegex = /(?:che(?:que)?|chq|dd|draft|gpay|upi|utr|neft|rtgs|bank|sbi|hdfc|icici|axis|canara|iob|pnb|kvb|tmb|citi|ledger|cash|mo|money\s*order)[\s.:#№-]*\d+|\b(?:che(?:que)?|chq|dd|draft|gpay|upi|utr|neft|rtgs)\b|\b\d{6}\b/i;

        const isChq1 = chequeRegex.test(r1);
        const isChq2 = chequeRegex.test(r2);
        const isChequeMode = (viewingSub.payment_mode || "").toLowerCase().includes("cheque") || 
                             (viewingSub.payment_mode || "").toLowerCase().includes("draft") ||
                             (viewingSub.payment_mode || "").toLowerCase().includes("offline");

        let verifiedInstrument = "";
        if (isChq1 && isChq2) {
          verifiedInstrument = `${r1} | ${r2}`;
        } else if (isChq1) {
          verifiedInstrument = r1;
        } else if (isChq2) {
          verifiedInstrument = r2;
        } else if (r1) {
          verifiedInstrument = r1;
        } else if (r2) {
          verifiedInstrument = r2;
        } else if (isChequeMode) {
          verifiedInstrument = `${viewingSub.payment_mode || "Cheque"} Payment Recorded`;
        }

        const showInstrumentCard = isChq1 || isChq2 || isChequeMode || r1.length > 0;
        const term = getSubscriberTermDates(viewingSub, false);
        const planInfo = getReceiptPlanInfo(viewingSub);

        return createPortal(
          <div className="sub-profile-view-overlay" onClick={() => setViewingSub(null)}>
            <div className="sub-profile-view-drawer" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="sub-profile-view-header">
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <i className="bi bi-person-badge-fill" style={{ color: "#dfb76c", fontSize: "1.4rem" }}></i>
                  <div>
                    <h3 style={{ margin: 0, color: "#ffffff", fontSize: "1.15rem", fontWeight: 750 }}>
                      Subscriber Profile Overview
                    </h3>
                    <p style={{ margin: "2px 0 0 0", color: "#fce7b2", fontSize: "0.8rem", fontFamily: "monospace" }}>
                      {viewingSub.login_id || "N/A"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="sub-profile-view-close"
                  onClick={() => setViewingSub(null)}
                  title="Close Profile"
                >
                  &times;
                </button>
              </div>

              {/* Scrollable Body */}
              <div className="sub-profile-view-body">
                {/* CRM Hero Summary Card */}
                <div className="sub-profile-hero-card">
                  <div className="sub-profile-avatar">
                    {((viewingSub.user_name || viewingSub.name || "S").charAt(0) || "S").toUpperCase()}
                  </div>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <h4 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "#4a1216" }}>
                      {viewingSub.user_name || viewingSub.name || "N/A"}
                    </h4>
                    <p style={{ margin: "2px 0 0 0", fontSize: "0.85rem", color: "#666", fontFamily: "monospace" }}>
                      ID: <strong>{viewingSub.login_id || "N/A"}</strong>
                    </p>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
                      {(Number(viewingSub.is_vip) === 1 || Number(viewingSub.user_is_vip) === 1) && (
                        <span style={{ background: "#854d0e", color: "#fef08a", border: "1px solid #ca8a04", padding: "1px 6px", borderRadius: "6px", fontSize: "10px", fontWeight: "800" }}>
                          ⭐ VIP
                        </span>
                      )}
                      {isAbroadSub(viewingSub) && (
                        <span style={{ background: "#0284c7", color: "#ffffff", padding: "1px 6px", borderRadius: "6px", fontSize: "10px", fontWeight: "800" }}>
                          🌐 Abroad
                        </span>
                      )}
                      {(Number(viewingSub.is_renewal) === 1 || viewingSub.last_renewed_at) && (
                        <span style={{ background: "#e0f2fe", color: "#0369a1", border: "1px solid #bae6fd", padding: "1px 6px", borderRadius: "6px", fontSize: "10px", fontWeight: "800" }}>
                          🔄 Renewal
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      backgroundColor: (viewingSub.status || "").toLowerCase() === "paid" ? "#e6f4ea" : (viewingSub.status || "").toLowerCase() === "paused" ? "#fef7e0" : "#f1f3f4",
                      color: (viewingSub.status || "").toLowerCase() === "paid" ? "#137333" : (viewingSub.status || "").toLowerCase() === "paused" ? "#b06000" : "#3c4043",
                      border: (viewingSub.status || "").toLowerCase() === "paid" ? "1px solid #c2e7c9" : (viewingSub.status || "").toLowerCase() === "paused" ? "1px solid #fde293" : "1px solid #dadce0"
                    }}
                  >
                    {viewingSub.status || "Created"}
                  </span>
                </div>

                {/* Remaining Validity Banner */}
                {(() => {
                  if (viewingSub.status === "paused") {
                    return (
                      <div style={{ background: "#fef7e0", color: "#b06000", border: "1px solid #fde293", borderRadius: "8px", padding: "8px 12px", fontSize: "12.5px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                        <i className="bi bi-pause-circle-fill"></i>
                        <span>Subscription is currently paused (temporarily cancelled).</span>
                      </div>
                    );
                  }
                  if (viewingSub.status === "cancelled") {
                    return (
                      <div style={{ background: "#fce8e6", color: "#c5221f", border: "1px solid #fad2cf", borderRadius: "8px", padding: "8px 12px", fontSize: "12.5px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                        <i className="bi bi-x-circle-fill"></i>
                        <span>Subscription is cancelled.</span>
                      </div>
                    );
                  }
                  if (viewingSub.end_date) {
                    const endD = new Date(viewingSub.end_date);
                    const todayD = new Date();
                    endD.setHours(0, 0, 0, 0);
                    todayD.setHours(0, 0, 0, 0);
                    const diffD = Math.ceil((endD.getTime() - todayD.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffD > 0) {
                      return (
                        <div style={{ background: "#e6f4ea", color: "#137333", border: "1px solid #c2e7c9", borderRadius: "8px", padding: "8px 12px", fontSize: "12.5px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                          <i className="bi bi-check-circle-fill"></i>
                          <span>Active: {diffD} days remaining (Validity: {term.startStr} to {term.endStr})</span>
                        </div>
                      );
                    } else {
                      return (
                        <div style={{ background: "#f1f3f4", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "8px 12px", fontSize: "12.5px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
                          <i className="bi bi-exclamation-triangle-fill" style={{ color: "#d97706" }}></i>
                          <span>Expired: Validity ended {Math.abs(diffD)} days ago (Term ended: {term.endStr})</span>
                        </div>
                      );
                    }
                  }
                  return null;
                })()}

                {/* Remarks & Cheque / Payment Instrument Verification (HIGHLIGHTED) */}
                <div className="sub-profile-view-card" style={{ border: "1.5px solid #dfb76c", background: "#fffdf9" }}>
                  <h5 className="sub-profile-view-card-title" style={{ color: "#78350f", marginBottom: "12px" }}>
                    <i className="bi bi-shield-check" style={{ color: "#d97706" }}></i> Verification & Remarks
                  </h5>

                  {/* PROMINENT CHEQUE / PAYMENT VERIFICATION HIGHLIGHT */}
                  {showInstrumentCard && (
                    <div style={{
                      background: "linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 100%)",
                      border: "1.5px solid #3b82f6",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      marginBottom: "14px",
                      boxShadow: "0 2px 6px rgba(59, 130, 246, 0.12)"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#1e40af", fontWeight: "800", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          <i className="bi bi-bank2" style={{ color: "#2563eb", fontSize: "15px" }}></i>
                          <span>Cheque / Payment Verification</span>
                        </div>
                        <span style={{ background: "#2563eb", color: "#ffffff", padding: "2px 8px", borderRadius: "12px", fontSize: "10px", fontWeight: "800", textTransform: "uppercase" }}>
                          Mode: {viewingSub.payment_mode || "Cheque / Offline"}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff", padding: "10px 12px", borderRadius: "8px", border: "1px solid #bfdbfe", flexWrap: "wrap", gap: "8px" }}>
                        <div>
                          <div style={{ fontSize: "10.5px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Cheque / Reference Number</div>
                          <div style={{ fontSize: "15px", fontWeight: "800", color: "#1e3a8a", fontFamily: "monospace", letterSpacing: "0.5px", marginTop: "2px" }}>
                            {verifiedInstrument || "Recorded"}
                          </div>
                        </div>
                        {isChq1 && (
                          <span style={{ fontSize: "11px", color: "#1e40af", background: "#dbeafe", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: "6px", fontWeight: "700" }}>
                            ✓ Shown in Remark 1
                          </span>
                        )}
                        {!isChq1 && isChq2 && (
                          <span style={{ fontSize: "11px", color: "#1e40af", background: "#dbeafe", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: "6px", fontWeight: "700" }}>
                            ✓ Shown in Remark 2
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Remark 1 (Dispatch Notes) */}
                  <div className="sub-profile-remark-card" style={{ marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                      <div className="sub-profile-remark-tag" style={{ color: "#b45309", margin: 0 }}>
                        <i className="bi bi-truck"></i> Remark 1 (Dispatch Notes)
                      </div>
                      {isChq1 && (
                        <span style={{ fontSize: "10px", background: "#dbeafe", color: "#1d4ed8", padding: "1px 6px", borderRadius: "4px", fontWeight: "700" }}>
                          🏷️ Cheque Details
                        </span>
                      )}
                    </div>
                    {r1 ? (
                      <div style={{ color: "#78350f", fontWeight: "600", fontSize: "13px", background: "#fef3c7", padding: "8px 12px", borderRadius: "6px", border: "1px solid #fde68a", wordBreak: "break-word" }}>
                        {r1}
                      </div>
                    ) : (
                      <div style={{ color: "#94a3b8", fontSize: "12.5px", fontStyle: "italic", background: "#f8fafc", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                        — None recorded
                      </div>
                    )}
                  </div>

                  {/* Remark 2 (Office / Special Notes) */}
                  <div className="sub-profile-remark-card cheque-card" style={{ marginBottom: viewingSub.gift_by_sub_id ? "10px" : "0" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                      <div className="sub-profile-remark-tag" style={{ color: "#1d4ed8", margin: 0 }}>
                        <i className="bi bi-journal-text"></i> Remark 2 (Office / Special Notes)
                      </div>
                      {isChq2 && (
                        <span style={{ fontSize: "10px", background: "#dbeafe", color: "#1d4ed8", padding: "1px 6px", borderRadius: "4px", fontWeight: "700" }}>
                          🏷️ Cheque Details
                        </span>
                      )}
                    </div>
                    {r2 ? (
                      <div style={{ color: "#1e3a8a", fontWeight: "700", fontSize: "13px", background: "#dbeafe", padding: "8px 12px", borderRadius: "6px", border: "1px solid #bfdbfe", wordBreak: "break-word" }}>
                        {r2}
                      </div>
                    ) : (
                      <div style={{ color: "#94a3b8", fontSize: "12.5px", fontStyle: "italic", background: "#f8fafc", padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0" }}>
                        — None recorded
                      </div>
                    )}
                  </div>

                  {/* Referral / Gifted By */}
                  {viewingSub.gift_by_sub_id && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#fef3c7", border: "1px solid #fde68a", padding: "8px 12px", borderRadius: "8px", fontSize: "12.5px", color: "#92400e", fontWeight: "700", marginTop: "10px" }}>
                      <i className="bi bi-gift-fill" style={{ fontSize: "15px" }}></i>
                      <span>Gifted / Referred by: <strong>{viewingSub.gift_by_sub_id}</strong></span>
                    </div>
                  )}
                </div>

                {/* Subscription Plan & Validity Details */}
                <div className="sub-profile-view-card">
                  <h5 className="sub-profile-view-card-title">
                    <i className="bi bi-journal-bookmark-fill"></i> Subscription Plan & Validity
                  </h5>
                  <div className="sub-profile-grid">
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Package / Plan</span>
                      <span className="sub-profile-grid-value">
                        {viewingSub.package_name || (planInfo && planInfo.title) || "Standard Plan"}
                      </span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Amount Paid</span>
                      <span className="sub-profile-grid-value" style={{ color: "#166534", fontWeight: "750" }}>₹{viewingSub.amount || 0}</span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Active Period</span>
                      <span className="sub-profile-grid-value" style={{ color: "#1e40af", fontWeight: "700" }}>
                        {term.startStr} - {term.endStr}
                      </span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Duration / Issues</span>
                      <span className="sub-profile-grid-value">
                        {(planInfo && planInfo.billingTerm) || (Number(viewingSub.amount) >= 1200 ? "2 Years (24 Issues)" : "1 Year (12 Issues)")}
                      </span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Payment Mode</span>
                      <span className="sub-profile-grid-value">{viewingSub.payment_mode || "Manual / Offline"}</span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Member Since</span>
                      <span className="sub-profile-grid-value">{term.memberSinceStr || "N/A"}</span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Delivery Mode</span>
                      <span className="sub-profile-grid-value" style={{ fontWeight: "650", color: "#334155" }}>
                        {viewingSub.dispatch_mode || "Indian Postal / General Post"}
                      </span>
                    </div>
                    {viewingSub.order_id && (
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">Order ID</span>
                        <span className="sub-profile-grid-value" style={{ fontFamily: "monospace", fontSize: "12px", background: "#f1f5f9", padding: "4px 8px", borderRadius: "6px", wordBreak: "break-all" }}>
                          {viewingSub.order_id}
                        </span>
                      </div>
                    )}
                    {viewingSub.payment_id && viewingSub.payment_id !== viewingSub.order_id && (
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">Payment / Transaction ID</span>
                        <span className="sub-profile-grid-value" style={{ fontFamily: "monospace", fontSize: "12px", background: "#f1f5f9", padding: "4px 8px", borderRadius: "6px", wordBreak: "break-all" }}>
                          {viewingSub.payment_id}
                        </span>
                      </div>
                    )}
                    {viewingSub.receipt_number && (
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">Receipt Voucher No</span>
                        <span className="sub-profile-grid-value" style={{ fontFamily: "monospace", fontSize: "12px", color: "#1e3a8a", background: "#e0f2fe", padding: "4px 8px", borderRadius: "6px", fontWeight: "750" }}>
                          {viewingSub.receipt_number}
                        </span>
                      </div>
                    )}
                    {viewingSub.last_renewed_at && !isNaN(new Date(viewingSub.last_renewed_at).getTime()) && (
                      <div className="sub-profile-grid-item" style={{ gridColumn: "span 2" }}>
                        <span className="sub-profile-grid-label">Last Renewed On</span>
                        <span className="sub-profile-grid-value" style={{ color: "#0369a1", fontWeight: "700" }}>
                          {new Date(viewingSub.last_renewed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="sub-profile-view-card">
                  <h5 className="sub-profile-view-card-title">
                    <i className="bi bi-telephone-fill"></i> Contact Details
                  </h5>
                  <div className="sub-profile-grid">
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Mobile Phone</span>
                      <span className="sub-profile-grid-value" style={{ color: "#0f172a", fontWeight: "700" }}>
                        {viewingSub.user_phone || viewingSub.phone || "N/A"}
                      </span>
                    </div>
                    <div className="sub-profile-grid-item">
                      <span className="sub-profile-grid-label">Landline</span>
                      <span className="sub-profile-grid-value">{viewingSub.landline || "N/A"}</span>
                    </div>
                    <div className="sub-profile-grid-item" style={{ gridColumn: "span 2" }}>
                      <span className="sub-profile-grid-label">Email Address</span>
                      <span className="sub-profile-grid-value" style={{ color: "#1e293b" }}>
                        {viewingSub.user_email || viewingSub.email || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Delivery Address */}
                <div className="sub-profile-view-card">
                  <h5 className="sub-profile-view-card-title">
                    <i className="bi bi-geo-alt-fill"></i> Delivery & Postal Address
                  </h5>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ fontSize: "13.5px", color: "#1e293b", lineHeight: "1.5", whiteSpace: "pre-wrap", background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                      {viewingSub.address || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No postal address on record</span>}
                    </div>
                    <div className="sub-profile-grid" style={{ marginTop: "6px" }}>
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">City</span>
                        <span className="sub-profile-grid-value">{viewingSub.city || "N/A"}</span>
                      </div>
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">Pincode</span>
                        <span className="sub-profile-grid-value" style={{ fontWeight: "750", color: "#0f766e" }}>{viewingSub.pincode || "N/A"}</span>
                      </div>
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">State</span>
                        <span className="sub-profile-grid-value">{viewingSub.state || "N/A"}</span>
                      </div>
                      <div className="sub-profile-grid-item">
                        <span className="sub-profile-grid-label">Country</span>
                        <span className="sub-profile-grid-value">{viewingSub.country || viewingSub.user_country || "India"}</span>
                      </div>
                    </div>

                    {/* Dispatch POD tracking info if present */}
                    {viewingSub.pod_number && (
                      <div style={{ marginTop: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "10px 12px", borderRadius: "8px" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", fontWeight: "750", color: "#166534", textTransform: "uppercase" }}>
                            <i className="bi bi-box-seam" style={{ marginRight: "4px" }}></i> Courier / Dispatch Tracking
                          </span>
                          {viewingSub.courier_partner && (
                            <span style={{ fontSize: "11px", color: "#166534", background: "#dcfce7", padding: "1px 6px", borderRadius: "4px", fontWeight: "700" }}>
                              {viewingSub.courier_partner}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "13px", fontWeight: "750", color: "#14532d", fontFamily: "monospace" }}>
                          POD / Tracking No: {viewingSub.pod_number}
                        </div>
                        {viewingSub.tracking_url && (
                          <a href={viewingSub.tracking_url} target="_blank" rel="noopener noreferrer" style={{ display: "inline-block", marginTop: "4px", fontSize: "11.5px", color: "#0284c7", textDecoration: "underline" }}>
                            Track Parcel Shipment &rarr;
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer with Actions */}
              <div className="sub-profile-footer">
                <button
                  type="button"
                  className="btn-drawer-cancel"
                  onClick={() => setViewingSub(null)}
                >
                  Close
                </button>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    style={{
                      padding: "9px 16px",
                      background: "#eff6ff",
                      color: "#1d4ed8",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px"
                    }}
                    onClick={() => {
                      const subToOpen = viewingSub;
                      setViewingSub(null);
                      handleOpenHistory(subToOpen);
                    }}
                    title="View Receipts & History"
                  >
                    <i className="bi bi-clock-history"></i> History
                  </button>
                  <button
                    type="button"
                    style={{
                      padding: "9px 18px",
                      background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 8px rgba(22, 101, 52, 0.25)"
                    }}
                    onClick={() => {
                      const subToEdit = viewingSub;
                      setViewingSub(null);
                      handleEditClick(subToEdit);
                    }}
                    title="Edit Subscriber Details"
                  >
                    <i className="bi bi-pencil-fill"></i> Edit Profile
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        );
      })()}

      {/* EDIT SUBSCRIBER DRAWER (SLIDE-OVER REDESIGN) */}
      {editingSub && createPortal(
        <div className="drawer-overlay" onClick={() => setEditingSub(null)}>
          <style>{`
            @keyframes slideInDrawer {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes fadeInOverlay {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            .drawer-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100vw;
              height: 100vh;
              background-color: rgba(24, 8, 10, 0.45);
              display: flex;
              justify-content: flex-end;
              z-index: 99999;
              animation: fadeInOverlay 0.3s ease-out forwards;
              backdrop-filter: blur(4px);
            }
            .drawer-container {
              background-color: #ffffff;
              width: 100%;
              max-width: 480px;
              height: 100vh;
              box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
              display: flex;
              flex-direction: column;
              animation: slideInDrawer 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              border-left: 1px solid rgba(0, 0, 0, 0.08);
              font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            }
            .drawer-header {
              background: linear-gradient(135deg, #4a1216 0%, #6b1d24 100%);
              padding: 20px 24px;
              display: flex;
              align-items: center;
              justify-content: space-between;
              border-bottom: 2px solid #dfb76c;
            }
            .drawer-title {
              color: #ffffff;
              margin: 0;
              font-size: 1.25rem;
              font-weight: 750;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .drawer-title i {
              color: #dfb76c;
            }
            .drawer-close {
              background: transparent;
              border: none;
              color: rgba(255, 255, 255, 0.8);
              font-size: 1.6rem;
              cursor: pointer;
              width: 32px;
              height: 32px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s;
            }
            .drawer-close:hover {
              background-color: rgba(255, 255, 255, 0.1);
              color: #ffffff;
              transform: rotate(90deg);
            }
            .drawer-body {
              padding: 24px;
              overflow-y: auto;
              flex: 1;
              background-color: #fdfdfd;
            }
            .drawer-section {
              background: #ffffff;
              border-radius: 12px;
              padding: 20px;
              border: 1px solid #ebdcb9;
              margin-bottom: 20px;
              box-shadow: 0 4px 12px rgba(93, 29, 43, 0.02);
            }
            .drawer-section-title {
              font-size: 0.85rem;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #8c7e69;
              font-weight: 750;
              margin: 0 0 16px 0;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .drawer-section-title i {
              color: #dfb76c;
            }
            .drawer-input-group {
              display: flex;
              flex-direction: column;
              gap: 6px;
              margin-bottom: 16px;
              text-align: left;
            }
            .drawer-input-group:last-child {
              margin-bottom: 0;
            }
            .drawer-input-group label {
              font-size: 0.8rem;
              font-weight: 650;
              color: #4a5568;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .drawer-input-with-icon {
              position: relative;
              display: flex;
              align-items: center;
            }
            .drawer-input-with-icon i {
              position: absolute;
              left: 12px;
              color: #a0aec0;
              font-size: 0.95rem;
            }
            .drawer-input-with-icon input,
            .drawer-input-with-icon select {
              width: 100%;
              padding: 10px 12px 10px 38px;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-size: 0.9rem;
              color: #2d3748;
              background-color: #ffffff;
              transition: all 0.2s;
              box-sizing: border-box;
            }
            .drawer-input-with-icon input:focus,
            .drawer-input-with-icon select:focus {
              outline: none;
              border-color: #6b1d24;
              box-shadow: 0 0 0 3px rgba(107, 29, 36, 0.15);
            }
            .drawer-footer {
              padding: 18px 24px;
              background-color: #ffffff;
              border-top: 1px solid #ebdcb9;
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.02);
            }
            .btn-drawer-cancel {
              padding: 10px 20px;
              border: 1px solid #cbd5e1;
              background-color: #ffffff;
              color: #475569;
              border-radius: 8px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
            }
            .btn-drawer-cancel:hover {
              background-color: #f1f5f9;
              color: #1e293b;
            }
            .btn-drawer-save {
              padding: 10px 24px;
              border: none;
              background: linear-gradient(135deg, #58181f 0%, #701e24 100%);
              color: #ffffff;
              border-radius: 8px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              box-shadow: 0 4px 10px rgba(88, 24, 31, 0.25);
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .btn-drawer-save:hover {
              transform: translateY(-1px);
              box-shadow: 0 6px 14px rgba(88, 24, 31, 0.35);
              background: linear-gradient(135deg, #4c141a 0%, #631a20 100%);
            }
            .subscriber-card-summary {
              background: #ffffff;
              border: 1px solid #ebdcb9;
              border-radius: 12px;
              padding: 16px;
              margin-bottom: 20px;
              display: flex;
              align-items: center;
              gap: 16px;
              box-shadow: 0 4px 12px rgba(93, 29, 43, 0.04);
            }
            .subscriber-avatar {
              width: 52px;
              height: 52px;
              border-radius: 50%;
              background: linear-gradient(135deg, #dfb76c 0%, #b8860b 100%);
              color: #4a1216;
              font-size: 1.3rem;
              font-weight: 700;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid #ffffff;
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }
            .subscriber-info-header {
              flex: 1;
              text-align: left;
            }
            .subscriber-name-title {
              margin: 0;
              font-size: 1.15rem;
              font-weight: 700;
              color: #4a1216;
            }
            .subscriber-id-subtitle {
              margin: 2px 0 0 0;
              font-size: 0.85rem;
              color: #666;
              font-family: monospace;
            }
            .subscriber-status-indicator {
              padding: 6px 12px;
              border-radius: 20px;
              font-size: 0.8rem;
              font-weight: 600;
              text-transform: uppercase;
            }
            .subscriber-status-indicator.paid {
              background-color: #e6f4ea;
              color: #137333;
              border: 1px solid #c2e7c9;
            }
            .subscriber-status-indicator.paused {
              background-color: #fef7e0;
              color: #b06000;
              border: 1px solid #fde293;
            }
            .subscriber-status-indicator.expired {
              background-color: #f1f3f4;
              color: #3c4043;
              border: 1px solid #dadce0;
            }
            .subscriber-status-indicator.cancelled {
              background-color: #fce8e6;
              color: #c5221f;
              border: 1px solid #fad2cf;
            }
            .remaining-banner {
              border-radius: 8px;
              padding: 10px 14px;
              margin-bottom: 20px;
              font-size: 0.85rem;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 8px;
              text-align: left;
            }
            .remaining-banner.active {
              background-color: rgba(22, 163, 74, 0.1);
              color: #16a34a;
              border: 1px solid rgba(22, 163, 74, 0.2);
            }
            .remaining-banner.expired {
              background-color: rgba(220, 38, 38, 0.1);
              color: #dc2626;
              border: 1px solid rgba(220, 38, 38, 0.2);
            }
            .remaining-banner.paused {
              background-color: rgba(217, 119, 6, 0.1);
              color: #d97706;
              border: 1px solid rgba(217, 119, 6, 0.2);
            }
            .subscriber-cell-name {
              display: flex;
              flex-direction: column;
              gap: 2px;
              font-weight: 700;
              color: #1e293b;
              text-align: left;
            }
            .subscriber-cell-email {
              font-size: 11px;
              color: #64748b;
              font-weight: 500;
            }
          `}</style>
          
          <div className="drawer-container" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                <i className="bi bi-person-lines-fill"></i> Subscriber Profile
              </h3>
              <button className="drawer-close" onClick={() => setEditingSub(null)}>&times;</button>
            </div>
            
            <form onSubmit={handleEditSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", margin: 0 }}>
              <div className="drawer-body">
                
                {/* Subscriber CRM Summary Card */}
                <div className="subscriber-card-summary">
                  <div className="subscriber-avatar">
                    {(editForm.user_name || "S").charAt(0).toUpperCase()}
                  </div>
                  <div className="subscriber-info-header">
                    <h4 className="subscriber-name-title">{editForm.user_name || "N/A"}</h4>
                    <p className="subscriber-id-subtitle">ID: {editForm.login_id || "N/A"}</p>
                  </div>
                  <span className={`subscriber-status-indicator ${editForm.status || "expired"}`}>
                    {editForm.status || "Unknown"}
                  </span>
                </div>
                
                {/* Dynamic Calculated Days Remaining Banner */}
                {getRemainingBanner()}
                
                {/* Section 1: Personal Profile */}
                <div className="drawer-section">
                  <h5 className="drawer-section-title">
                    <i className="bi bi-person-circle"></i> Personal Profile
                  </h5>
                  
                  <div className="drawer-input-group">
                    <label>Subscription ID</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-card-text"></i>
                      <input
                        type="text"
                        value={editForm.login_id}
                        onChange={(e) => setEditForm({ ...editForm, login_id: e.target.value })}
                        placeholder="e.g. VSRI_1001"
                      />
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label>Full Name</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-person"></i>
                      <input
                        type="text"
                        value={editForm.user_name}
                        onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                        placeholder="Subscriber Name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>Email Address</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", background: "#f1f5f9", padding: "2px 8px", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                        🔒 Registered Account (Read-Only)
                      </span>
                    </label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-envelope"></i>
                      <input
                        type="email"
                        value={editForm.user_email}
                        readOnly
                        disabled
                        style={{ backgroundColor: "#f8fafc", color: "#64748b", cursor: "not-allowed" }}
                        title="Subscriber email is tied to their login account authentication"
                      />
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label>Phone Number</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-telephone"></i>
                      <input
                        type="text"
                        value={editForm.user_phone || ""}
                        onChange={(e) => setEditForm({ ...editForm, user_phone: e.target.value })}
                        placeholder="Phone Number (10 digits)"
                      />
                    </div>
                  </div>

                  <div className="drawer-input-group">
                    <label>Landline Number (Optional)</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-telephone-plus"></i>
                      <input
                        type="text"
                        value={editForm.landline}
                        onChange={(e) => setEditForm({ ...editForm, landline: e.target.value })}
                        placeholder="e.g. 044-24474312"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Address & Dispatch */}
                <div className="drawer-section">
                  <h5 className="drawer-section-title">
                    <i className="bi bi-geo-alt"></i> Address & Dispatch Details
                  </h5>

                  <div className="drawer-input-group">
                    <label>Postal Address</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-house-door"></i>
                      <input
                        type="text"
                        value={editForm.address}
                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                        placeholder="Door No, Street, Locality"
                      />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                    <div className="drawer-input-group">
                      <label>City / Place</label>
                      <div className="drawer-input-with-icon">
                        <i className="bi bi-building"></i>
                        <input
                          type="text"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                    </div>
                    <div className="drawer-input-group">
                      <label>Pincode</label>
                      <div className="drawer-input-with-icon">
                        <i className="bi bi-pin-map"></i>
                        <input
                          type="text"
                          value={editForm.pincode}
                          onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })}
                          placeholder="600001"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="drawer-input-group" style={{ marginTop: "10px" }}>
                    <label>State (Optional)</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-map"></i>
                      <input
                        type="text"
                        value={editForm.state}
                        onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                        placeholder="e.g. Tamil Nadu"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Section 3: Subscription & Billing */}
                <div className="drawer-section">
                  <h5 className="drawer-section-title">
                    <i className="bi bi-credit-card-2-front"></i> Plan & Billing
                  </h5>
                  
                  <div className="drawer-input-group">
                    <label>Subscription Plan</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-bookmark-star"></i>
                      <select
                        value={editForm.plan_id}
                        onChange={(e) => setEditForm({ ...editForm, plan_id: e.target.value })}
                        required
                      >
                        <option value="">Select Plan</option>
                        {plans.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.monthly_title} / {p.annual_title} (ID: {p.id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label>Amount Paid (₹)</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-currency-rupee"></i>
                      <input
                        type="number"
                        step="0.01"
                        value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                        placeholder="0.00"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label>Payment Status</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-info-circle"></i>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        required
                      >
                        <option value="paid">Paid</option>
                        <option value="paused">Paused (Temp Cancelled)</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <div className="drawer-input-group">
                    <label>Order ID</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-receipt"></i>
                      <input
                        type="text"
                        value={editForm.order_id}
                        onChange={(e) => setEditForm({ ...editForm, order_id: e.target.value })}
                        placeholder="Link Order ID (e.g. order_...)"
                      />
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <label style={{ fontWeight: "700", color: "#334155" }}>
                      <i className="bi bi-calendar-check" style={{ color: "#16a34a", marginRight: "6px" }}></i>
                      Start Issue (Month & Year) *
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "8px" }}>
                      <select
                        value={editForm.start_month}
                        onChange={(e) => {
                          const newSM = parseInt(e.target.value, 10);
                          const currentTotal = (Number(editForm.end_year) - Number(editForm.start_year)) * 12 + (Number(editForm.end_month) - Number(editForm.start_month)) + 1;
                          const is4Y = currentTotal >= 40 || (editingSub?.package_name || "").toUpperCase().includes("4 YEAR");
                          const is2Y = !is4Y && (currentTotal >= 20 || editForm.plan_id === "2" || (editingSub?.package_name || "").toUpperCase().includes("2 YEAR") || Number(editingSub?.amount) >= 1200 || Number(editingSub?.is_annual) === 1);
                          const totalMonths = is4Y ? 48 : (is2Y ? 24 : 12);
                          const addMonths = totalMonths - 1;
                          const newEM = ((newSM - 1 + addMonths) % 12) + 1;
                          const newEY = editForm.start_year + Math.floor((newSM - 1 + addMonths) / 12);
                          setEditForm({ ...editForm, start_month: newSM, end_month: newEM, end_year: newEY });
                        }}
                        style={{ padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", fontWeight: "600", color: "#1e293b", background: "#ffffff" }}
                      >
                        {MONTH_OPTIONS.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={editForm.start_year}
                        onChange={(e) => {
                          const newSY = parseInt(e.target.value, 10);
                          const currentTotal = (Number(editForm.end_year) - Number(editForm.start_year)) * 12 + (Number(editForm.end_month) - Number(editForm.start_month)) + 1;
                          const is4Y = currentTotal >= 40 || (editingSub?.package_name || "").toUpperCase().includes("4 YEAR");
                          const is2Y = !is4Y && (currentTotal >= 20 || editForm.plan_id === "2" || (editingSub?.package_name || "").toUpperCase().includes("2 YEAR") || Number(editingSub?.amount) >= 1200 || Number(editingSub?.is_annual) === 1);
                          const totalMonths = is4Y ? 48 : (is2Y ? 24 : 12);
                          const addMonths = totalMonths - 1;
                          const newEY = newSY + Math.floor((editForm.start_month - 1 + addMonths) / 12);
                          setEditForm({ ...editForm, start_year: newSY, end_year: newEY });
                        }}
                        style={{ padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", fontWeight: "600", color: "#1e293b", background: "#ffffff" }}
                      >
                        {Array.from({ length: 25 }, (_, i) => 2015 + i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="drawer-input-group">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                      <label style={{ fontWeight: "700", color: "#334155", margin: 0 }}>
                        <i className="bi bi-calendar-x" style={{ color: "#dc2626", marginRight: "6px" }}></i>
                        End Issue (Month & Year) *
                      </label>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => {
                            const sM = parseInt(editForm.start_month, 10);
                            const sY = parseInt(editForm.start_year, 10);
                            const eM = ((sM - 1 + 11) % 12) + 1;
                            const eY = sY + Math.floor((sM - 1 + 11) / 12);
                            setEditForm({ ...editForm, end_month: eM, end_year: eY });
                          }}
                          style={{ fontSize: "11px", padding: "2px 8px", background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: "4px", cursor: "pointer", fontWeight: "700" }}
                          title="Set to 1 Year (12 issues: e.g. Sept to Aug)"
                        >
                          +1 Year (12 issues)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const sM = parseInt(editForm.start_month, 10);
                            const sY = parseInt(editForm.start_year, 10);
                            const eM = ((sM - 1 + 23) % 12) + 1;
                            const eY = sY + Math.floor((sM - 1 + 23) / 12);
                            setEditForm({ ...editForm, end_month: eM, end_year: eY });
                          }}
                          style={{ fontSize: "11px", padding: "2px 8px", background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: "4px", cursor: "pointer", fontWeight: "700" }}
                          title="Set to 2 Years (24 issues: e.g. Sept to Aug)"
                        >
                          +2 Years (24 issues)
                        </button>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "8px" }}>
                      <select
                        value={editForm.end_month}
                        onChange={(e) => setEditForm({ ...editForm, end_month: parseInt(e.target.value, 10) })}
                        style={{ padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", fontWeight: "600", color: "#1e293b", background: "#ffffff" }}
                      >
                        {MONTH_OPTIONS.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        value={editForm.end_year}
                        onChange={(e) => setEditForm({ ...editForm, end_year: parseInt(e.target.value, 10) })}
                        style={{ padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "14px", fontWeight: "600", color: "#1e293b", background: "#ffffff" }}
                      >
                        {Array.from({ length: 25 }, (_, i) => 2015 + i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Live Period Preview Banner */}
                  <div style={{ background: "#fdf8ee", border: "1.5px solid #dfb76c", borderRadius: "8px", padding: "10px 14px", marginTop: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="bi bi-info-circle-fill" style={{ color: "#b45309", fontSize: "1.1rem" }}></i>
                    <div>
                      <div style={{ fontSize: "11px", color: "#92400e", fontWeight: "700", textTransform: "uppercase" }}>Selected Issue Validity Period</div>
                      <div style={{ fontSize: "13px", fontWeight: "800", color: "#78350f" }}>
                        {MONTH_OPTIONS.find(m => m.value === Number(editForm.start_month))?.label} {editForm.start_year} to {MONTH_OPTIONS.find(m => m.value === Number(editForm.end_month))?.label} {editForm.end_year}
                        {(() => {
                          const total = (Number(editForm.end_year) - Number(editForm.start_year)) * 12 + (Number(editForm.end_month) - Number(editForm.start_month)) + 1;
                          return total > 0 ? ` (${total} Magazine Issues)` : "";
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Remarks & Referral */}
                <div className="drawer-section">
                  <h5 className="drawer-section-title">
                    <i className="bi bi-chat-left-text"></i> Remarks & Referral
                  </h5>

                  <div className="drawer-input-group">
                    <label>Remark 1 (Dispatch Notes)</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-truck"></i>
                      <input
                        type="text"
                        value={editForm.remarks_1}
                        onChange={(e) => setEditForm({ ...editForm, remarks_1: e.target.value })}
                        placeholder="e.g. Hand Delivery, Abroad Air Mail, Landmark"
                      />
                    </div>
                  </div>

                  <div className="drawer-input-group">
                    <label>Remark 2 (Office / Special Notes)</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-journal-text"></i>
                      <input
                        type="text"
                        value={editForm.remarks_2}
                        onChange={(e) => setEditForm({ ...editForm, remarks_2: e.target.value })}
                        placeholder="e.g. Special Edition, Devotee Note"
                      />
                    </div>
                  </div>

                  <div className="drawer-input-group">
                    <label>Gifted / Referred By ID</label>
                    <div className="drawer-input-with-icon">
                      <i className="bi bi-gift"></i>
                      <input
                        type="text"
                        value={editForm.gift_by_sub_id}
                        onChange={(e) => setEditForm({ ...editForm, gift_by_sub_id: e.target.value })}
                        placeholder="e.g. VS1045 or Referrer Name"
                      />
                    </div>
                  </div>
                </div>
                
              </div>
              
              <div className="drawer-footer">
                <button
                  type="button"
                  className="btn-drawer-cancel"
                  onClick={() => setEditingSub(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-drawer-save"
                >
                  <i className="bi bi-check-circle"></i> Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* ================= PRINT RECEIPT PORTAL ================= */}
      {viewingReceiptSub && createPortal(
        <div className="receipt-modal-overlay" onClick={() => setViewingReceiptSub(null)}>
          <div className="receipt-modal-container" onClick={(e) => e.stopPropagation()}>
            
            {/* MODAL ACTION BAR */}
            <div className="receipt-modal-header">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-file-earmark-text-fill fs-5" style={{ color: "#dfb76c" }}></i>
                <h3 className="m-0 fs-6 fw-bold text-white">Subscription Receipt Voucher</h3>
              </div>
              <button className="receipt-modal-close-btn" onClick={() => setViewingReceiptSub(null)}>&times;</button>
            </div>
            
            <div className="receipt-modal-body">
              <div className="receipt-print-area">
                <div className="official-receipt-document">
                  
                  {/* WATERMARK STAMP */}
                  <div className="receipt-watermark">VAITHIKASRI</div>

                  {/* TOP ORNAMENTAL BANNER */}
                  <div className="receipt-top-banner">
                    
                    {/* BRANDING IDENTITY */}
                    <div className="receipt-brand-identity">
                      <img 
                        src="/images/logo.jpg" 
                        alt="Vaithikasri Logo" 
                        className="receipt-brand-logo"
                      />
                      <div className="receipt-brand-text">
                        <h1 className="brand-name">VAITHIKASRI</h1>
                        <span className="brand-tamil">வைதிகஸ்ரீ</span>
                        <p className="brand-meta">
                          Vaithikasri Publications LLP<br />
                          New No: 488, Old No 175, T.T.K. Road, Alwarpet, Chennai - 600018,<br />
                          <span style={{ whiteSpace: "nowrap" }}>Tamil Nadu, India</span><br />
                          Email: <strong>vaidikasri@gmail.com</strong> | Web: <strong>www.vaithikasri.com</strong>
                        </p>
                      </div>
                    </div>

                    {/* RECEIPT BADGE BLOCK */}
                    <div className="receipt-title-block">
                      <div className="receipt-type-badge">OFFICIAL RECEIPT</div>
                      <div className="receipt-meta-box">
                        <div className="meta-line">
                          <span className="lbl">Receipt No:</span>
                          <span className="val highlight">{viewingReceiptSub.receipt_number || `VS-RCP-${String(viewingReceiptSub.id).padStart(5, "0")}`}</span>
                        </div>
                        <div className="meta-line">
                          <span className="lbl">Date:</span>
                          <span className="val">
                            {viewingReceiptSub.receipt_date 
                              ? new Date(viewingReceiptSub.receipt_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                              : viewingReceiptSub.created_at
                              ? new Date(viewingReceiptSub.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                              : viewingReceiptSub.start_date 
                              ? new Date(viewingReceiptSub.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) 
                              : new Date().toLocaleDateString("en-IN")}
                          </span>
                        </div>
                        <div className="meta-line">
                          <span className="lbl">Status:</span>
                          {viewingReceiptSub.is_pending_voucher ? (
                            <span className="badge-paid-stamp" style={{ background: "#fef3c7", color: "#92400e", borderColor: "#f59e0b" }}>
                              ⏳ PENDING APPROVAL
                            </span>
                          ) : (
                            <span className="badge-paid-stamp">✓ PAID</span>
                          )}
                        </div>
                      </div>
                    </div>

                  </div>

                  <div className="receipt-gold-divider"></div>

                  {/* 2-COLUMN SUBSCRIBER & PAYMENT AUDIT GRID */}
                  <div className="receipt-info-grid">
                    
                    {/* SUBSCRIBER INFO */}
                    <div className="info-card">
                      <div className="card-header-bar">
                        <i className="bi bi-person-check-fill me-1"></i> BILLED TO / SUBSCRIBER DETAILS
                      </div>
                      <div className="card-content-body">
                        <h4 className="subscriber-name">{viewingReceiptSub.user_name || "Subscriber"}</h4>
                        
                        <div className="info-detail-row">
                          <span className="dt-label">Member ID:</span>
                          <span className="dt-value fw-bold text-maroon">{viewingReceiptSub.login_id || "VS" + viewingReceiptSub.id}</span>
                        </div>
                        
                        <div className="info-detail-row">
                          <span className="dt-label">Email:</span>
                          <span className="dt-value">{viewingReceiptSub.user_email || "N/A"}</span>
                        </div>

                        <div className="info-detail-row">
                          <span className="dt-label">Contact:</span>
                          <span className="dt-value">{viewingReceiptSub.user_phone || "N/A"}</span>
                        </div>

                        <div className="info-detail-row align-items-start">
                          <span className="dt-label">Delivery Address:</span>
                          <span className="dt-value address-text">
                            {[
                              viewingReceiptSub.address,
                              viewingReceiptSub.city,
                              viewingReceiptSub.pincode ? `PIN: ${viewingReceiptSub.pincode}` : ""
                            ].filter(Boolean).join(", ") || "Address recorded on subscriber record"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* PAYMENT & AUDIT */}
                    <div className="info-card">
                      <div className="card-header-bar">
                        <i className="bi bi-credit-card-2-front-fill me-1"></i> PAYMENT & SUBSCRIPTION AUDIT
                      </div>
                      <div className="card-content-body">
                        
                        <div className="info-detail-row">
                          <span className="dt-label">Payment Mode:</span>
                          <span className="dt-value fw-bold">{viewingReceiptSub.payment_mode || "Online Gateway (Razorpay)"}</span>
                        </div>

                        <div className="info-detail-row">
                          <span className="dt-label">Transaction Ref:</span>
                          <span className="dt-value monospace-pill">{viewingReceiptSub.payment_id || "N/A"}</span>
                        </div>

                        <div className="info-detail-row">
                          <span className="dt-label">Validity Period:</span>
                          <span className="dt-value">
                            {formatSubPeriod(viewingReceiptSub.start_date, viewingReceiptSub.end_date, getReceiptPlanInfo(viewingReceiptSub).is2Years ? 1 : 0, " — ", false, viewingReceiptSub)}
                          </span>
                        </div>

                        <div className="info-detail-row">
                          <span className="dt-label">Dispatch Mode:</span>
                          <span className="dt-value fw-bold" style={{ color: "#5d1d2b" }}>{getDispatchMode(viewingReceiptSub)}</span>
                        </div>

                        {viewingReceiptSub.pod_number && (
                          <div className="info-detail-row">
                            <span className="dt-label">Postal POD:</span>
                            <span className="dt-value fw-bold text-success">{viewingReceiptSub.pod_number}</span>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* ITEMIZATION TABLE */}
                  {(() => {
                    const totalAmt = Number(viewingReceiptSub.amount || 0);
                    const feeAmt = Number(viewingReceiptSub.platform_fee || 0) || ((totalAmt % 100 === 50) ? 50 : 0);
                    const baseAmt = (feeAmt > 0 && totalAmt > feeAmt) ? (totalAmt - feeAmt) : totalAmt;

                    return (
                      <div className="receipt-table-wrapper">
                        <table className="official-receipt-table">
                          <thead>
                            <tr>
                              <th style={{ width: "5%", textAlign: "center" }}>#</th>
                              <th style={{ width: "55%" }}>Subscription Description & Service</th>
                              <th style={{ width: "15%", textAlign: "center" }}>Billing Term</th>
                              <th style={{ width: "10%", textAlign: "center" }}>Qty</th>
                              <th style={{ width: "15%", textAlign: "right" }}>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="text-center fw-bold" style={{ color: "#5d1d2b" }}>1</td>
                              <td>
                                {(() => {
                                  const rInfo = getReceiptPlanInfo(viewingReceiptSub);
                                  return (
                                    <>
                                      <div className="table-item-title">
                                        {rInfo.title}
                                      </div>
                                      <div className="table-item-sub">
                                        • Includes {rInfo.issuesCount} printed monthly issues delivered directly to subscriber address<br />
                                        • Active Period: {formatSubPeriod(viewingReceiptSub.start_date, viewingReceiptSub.end_date, rInfo.is2Years ? 1 : 0, " to ", false, viewingReceiptSub)}
                                      </div>
                                    </>
                                  );
                                })()}
                              </td>
                              <td className="text-center">{getReceiptPlanInfo(viewingReceiptSub).billingTerm}</td>
                              <td className="text-center fw-bold">1</td>
                              <td className="text-end fw-bold">₹{baseAmt.toLocaleString('en-IN')}.00</td>
                            </tr>
                          </tbody>
                          <tfoot>
                            <tr className="subtotal-row">
                              <td colSpan="4" className="text-end fw-bold">Subtotal</td>
                              <td className="text-end fw-bold">₹{baseAmt.toLocaleString('en-IN')}.00</td>
                            </tr>
                            <tr className="postage-row">
                              <td colSpan="4" className="text-end fw-bold">Postal Packaging & Delivery Charges</td>
                              <td className="text-end text-success fw-bold">INCLUDED (FREE)</td>
                            </tr>
                            {feeAmt > 0 && (
                              <tr className="postage-row">
                                <td colSpan="4" className="text-end fw-bold">Platform Convenience Fee</td>
                                <td className="text-end fw-bold">₹{feeAmt.toLocaleString('en-IN')}.00</td>
                              </tr>
                            )}
                            <tr className="grand-total-row">
                              <td colSpan="3" className="words-col">
                                <span className="words-label">Amount in Words:</span>
                                <span className="words-val">{convertNumberToWords(totalAmt)}</span>
                              </td>
                              <td className="text-end total-label">Total Paid:</td>
                              <td className="text-end total-val">₹{totalAmt.toLocaleString('en-IN')}.00</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    );
                  })()}

                  {/* FOOTER & OFFICIAL SEAL */}
                  <div className="receipt-bottom-grid">
                    
                    <div className="receipt-terms-box">
                      <h6 className="terms-title"><i className="bi bi-info-circle-fill me-1"></i> Delivery & Support Notes:</h6>
                      <ul className="terms-list">
                        <li>Upcoming magazine are dispatched during the previous calendar month.</li>
                        <li>Address modification can be updated in subscriber record before 10th of every month.</li>
                        <li>This is a computer-generated official receipt and requires no physical signature.</li>
                      </ul>
                    </div>

                    <div className="receipt-signature-box">
                      <div className="official-stamp-seal">
                        <div className="seal-inner">
                          <span className="seal-star">★ ★ ★</span>
                          <span className="seal-center">VAITHIKASRI<br />OFFICIAL</span>
                          <span className="seal-check">✓</span>
                        </div>
                      </div>
                      <div className="signatory-text">
                        <p className="m-0 fw-bold text-maroon" style={{ fontSize: "12px" }}>Vaithikasri Publications</p>
                        <p className="m-0 text-muted" style={{ fontSize: "10.5px" }}>Authorized Electronic Receipt</p>
                      </div>
                    </div>

                  </div>

                  {/* BOTTOM DECORATIVE FOOTER */}
                  <div className="receipt-bottom-bar">
                    <span>Thank you for supporting Vaithikasri • Dedicated to preserving Vedic Culture & Heritage</span>
                  </div>

                </div>
              </div>
            </div>
            
            <div className="receipt-modal-footer">
              <button 
                type="button"
                className="receipt-btn-close" 
                onClick={() => setViewingReceiptSub(null)}
              >
                Close
              </button>
              <button 
                type="button"
                className="receipt-btn-print" 
                onClick={() => window.print()}
              >
                <i className="bi bi-printer-fill me-1"></i> Print Receipt / Save PDF
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

      {/* ================= POD TRACKING MODAL ================= */}
      {podModalSub && (
        <div className="modal-overlay" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.65)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)", padding: "15px" }}>
          <div className="bg-white p-4 rounded-3 shadow-lg text-start" style={{ maxWidth: "480px", width: "100%", maxHeight: "90vh", overflowY: "auto", borderTop: "5px solid #5d1d2b" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="m-0 fw-bold" style={{ color: "#5d1d2b" }}><i className="bi bi-truck me-2" style={{ color: "#bc9341" }}></i> Dispatch POD Consignment</h4>
              <span 
                role="button" 
                onClick={() => setPodModalSub(null)}
                style={{ cursor: "pointer", fontSize: "24px", color: "#5d1d2b", fontWeight: "bold", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "1", borderRadius: "50%", background: "#f8efe4", transition: "background 0.2s" }}
                title="Close Modal"
              >
                &times;
              </span>
            </div>
            
            <div className="small text-muted mb-3" style={{ background: "#fdfbf9", padding: "12px", borderRadius: "8px", border: "1px solid #eee0c9" }}>
              <div style={{ fontWeight: "700", color: "#5d1d2b" }}>Subscriber: {podModalSub.user_name} ({podModalSub.login_id || `SUB-${podModalSub.id}`})</div>
              <div>Phone: {podModalSub.user_phone || "N/A"} | Email: {podModalSub.user_email || "N/A"}</div>
            </div>

            <form onSubmit={handleSavePod}>
              <div className="form-group mb-3">
                <label style={{ fontWeight: "700", color: "#4a1520", fontSize: "13px", display: "block", marginBottom: "6px" }}>Courier / Delivery Partner</label>
                <select
                  style={{ width: "100%", height: "42px", padding: "8px 12px", borderRadius: "8px", border: "1.5px solid #bc9341", fontSize: "14px", fontWeight: "600", color: "#222", backgroundColor: "#ffffff", cursor: "pointer", outline: "none", display: "block" }}
                  value={podForm.courier_partner}
                  onChange={(e) => setPodForm({ ...podForm, courier_partner: e.target.value })}
                >
                  <option value="India Post (Speed Post)">India Post (Speed Post)</option>
                  <option value="India Post EMS Airmail (International)">India Post EMS Airmail (International)</option>
                  <option value="DHL Express (International)">DHL Express (International)</option>
                  <option value="FedEx Express (International)">FedEx Express (International)</option>
                  <option value="Aramex Overseas">Aramex Overseas</option>
                  <option value="ST Courier">ST Courier</option>
                  <option value="DTDC Courier">DTDC Courier</option>
                  <option value="Professional Couriers">Professional Couriers</option>
                  <option value="Blue Dart">Blue Dart</option>
                  <option value="Delhivery">Delhivery</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div className="form-group mb-3">
                <label style={{ fontWeight: "700", color: "#4a1520", fontSize: "13px", display: "block", marginBottom: "6px" }}>POD / Consignment Tracking Number *</label>
                <input
                  type="text"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "2px solid #5d1d2b", fontFamily: "monospace", fontSize: "16px", fontWeight: "bold", color: "#111", background: "#fffdf9" }}
                  placeholder="e.g. SP123456789IN"
                  value={podForm.pod_number}
                  onChange={(e) => setPodForm({ ...podForm, pod_number: e.target.value })}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label style={{ fontWeight: "700", color: "#4a1520", fontSize: "13px", display: "block", marginBottom: "6px" }}>Tracking Website URL (Optional)</label>
                <input
                  type="url"
                  style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", background: "#ffffff" }}
                  placeholder="https://www.indiapost.gov.in/"
                  value={podForm.tracking_url}
                  onChange={(e) => setPodForm({ ...podForm, tracking_url: e.target.value })}
                />
                <small style={{ fontSize: "11px", color: "#64748b", display: "block", marginTop: "4px" }}>Leave blank to use default courier portal (https://www.indiapost.gov.in/)</small>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
                <button 
                  type="button" 
                  style={{ background: "#ffffff", color: "#5d1d2b", border: "1.5px solid #cbd5e1", minWidth: "100px", height: "38px", padding: "0 18px", borderRadius: "6px", fontWeight: "bold", fontSize: "13px", cursor: "pointer" }} 
                  onClick={() => setPodModalSub(null)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={{ background: "#5d1d2b", color: "#ffffff", border: "none", minWidth: "140px", height: "38px", padding: "0 22px", borderRadius: "6px", fontWeight: "bold", fontSize: "13px", cursor: "pointer", boxShadow: "0 2px 6px rgba(93, 29, 43, 0.25)" }} 
                  disabled={savingPod}
                >
                  {savingPod ? "Saving..." : "Save POD Details"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= 20-YEAR RECEIPTS HISTORY MODAL ================= */}
      {historySub && createPortal(
        <div 
          onClick={() => setHistorySub(null)} 
          style={{ 
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(24, 8, 10, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
            animation: "fadeInOverlay 0.2s ease-out"
          }}
        >
          <div 
            style={{
              backgroundColor: "#ffffff",
              width: "95%",
              maxWidth: "960px",
              maxHeight: "90vh",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              fontFamily: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              animation: "fadeInOverlay 0.25s ease-out"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ background: "linear-gradient(135deg, #3b0f15 0%, #6b1d24 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #dfb76c" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "rgba(223, 183, 108, 0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#dfb76c", fontSize: "1.3rem" }}>
                  <i className="bi bi-clock-history"></i>
                </div>
                <div>
                  <h3 style={{ color: "#ffffff", margin: 0, fontSize: "1.2rem", fontWeight: "750", display: "flex", alignItems: "center", gap: "8px" }}>
                    {historySub.user_name || "Subscriber"}
                    <span style={{ fontSize: "0.85rem", background: "rgba(255,255,255,0.2)", padding: "2px 8px", borderRadius: "6px", color: "#dfb76c" }}>
                      {historySub.login_id || "N/A"}
                    </span>
                    {(Number(historySub.is_vip) === 1 || Number(historySub.user_is_vip) === 1) && (
                      <span style={{ background: "#854d0e", color: "#fef08a", border: "1px solid #ca8a04", padding: "1px 6px", borderRadius: "6px", fontSize: "11px", fontWeight: "800" }}>
                        ⭐ VIP / Complimentary
                      </span>
                    )}
                  </h3>
                  <p style={{ margin: "2px 0 0", color: "rgba(255, 255, 255, 0.75)", fontSize: "0.82rem" }}>
                    {historySub.address ? `${historySub.address}, ` : ""}{historySub.city ? `${historySub.city}, ` : ""}{historySub.state ? `${historySub.state} - ` : ""}{historySub.pincode || ""} | Ph: {historySub.user_phone || historySub.landline || "N/A"}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setHistorySub(null)}
                style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#ffffff", width: "34px", height: "34px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, backgroundColor: "#f8fafc" }}>
              {/* Summary Stats Row */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px", marginBottom: "20px" }}>
                <div style={{ background: "#ffffff", padding: "14px 18px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                  <span style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: "700" }}>Total Renewal Receipts</span>
                  <div style={{ fontSize: "1.4rem", fontWeight: "800", color: "#1e293b", marginTop: "4px" }}>{subHistoryList.length}</div>
                </div>
                <div style={{ background: "#ffffff", padding: "14px 18px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                  <span style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: "700" }}>Lifetime Amount Paid</span>
                  <div style={{ fontSize: "1.4rem", fontWeight: "800", color: "#15803d", marginTop: "4px" }}>
                    ₹{subHistoryList.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toLocaleString("en-IN")}
                  </div>
                </div>
                <div style={{ background: "#ffffff", padding: "14px 18px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
                  <span style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: "700" }}>Current Status</span>
                  <div style={{ fontSize: "1.1rem", fontWeight: "800", color: historySub.is_active === 1 ? "#15803d" : "#64748b", marginTop: "6px" }}>
                    {historySub.is_active === 1 ? "🟢 Active Subscriber" : "⚪ Expired / Inactive"}
                  </div>
                </div>
              </div>

              {/* History Table */}
              <div style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 6px rgba(0,0,0,0.03)" }}>
                <div style={{ padding: "12px 18px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h4 style={{ margin: 0, fontSize: "0.95rem", fontWeight: "750", color: "#334155" }}>
                    📜 Complete Renewal History ({subHistoryList.length} Records)
                  </h4>
                  <span style={{ fontSize: "0.8rem", color: "#64748b" }}>Sorted by newest first</span>
                </div>

                {loadingHistory ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                    <i className="bi bi-arrow-repeat spin" style={{ fontSize: "1.8rem", display: "block", marginBottom: "8px" }}></i>
                    Loading historical receipts...
                  </div>
                ) : subHistoryList.length === 0 ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                    No payment history receipts recorded for this member.
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", textAlign: "left" }}>
                      <thead>
                        <tr style={{ background: "#f1f5f9", color: "#475569", borderBottom: "1px solid #cbd5e1" }}>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>#</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Receipt Date</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Receipt No</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Package / Plan</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Amount Paid</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Duration (From - To)</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Payment Mode</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700" }}>Status</th>
                          <th style={{ padding: "10px 14px", fontWeight: "700", textAlign: "center" }}>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subHistoryList.map((rec, idx) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);

                          const startDate = rec.start_date ? new Date(rec.start_date) : null;
                          startDate && startDate.setHours(0, 0, 0, 0);

                          const endDate = rec.end_date ? new Date(rec.end_date) : null;
                          endDate && endDate.setHours(23, 59, 59, 999);

                          const isUpcoming = startDate && startDate > today;
                          const isCurrentActive = !isUpcoming && endDate && endDate >= today;

                          const fmtDate = (d) => {
                            if (!d) return "—";
                            try {
                              const dt = new Date(d);
                              return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
                            } catch {
                              return String(d);
                            }
                          };

                          const fmtHistoryPeriod = (startDateStr, endDateStr, recItem) => {
                            if (!startDateStr && !endDateStr && !recItem?.start_month && !recItem?.end_month) return "—";
                            const startStr = recItem?.start_month && !/^\d{4}-\d{2}/.test(recItem.start_month) ? recItem.start_month : formatSubStartMonth(startDateStr);
                            let endStr = recItem?.end_month && !/^\d{4}-\d{2}/.test(recItem.end_month) ? recItem.end_month : formatSubEndMonth(startDateStr, endDateStr, (recItem?.package_name || "").includes("2 YEAR") || Number(recItem?.amount) >= 1200 ? 1 : 0);

                            if (startStr === "N/A" && endStr === "N/A") return "—";
                            if (startStr === "N/A") return endStr;
                            if (endStr === "N/A") return startStr;

                            // Magazine validity period rule: Start and End month can NEVER have identical month name
                            const pS = parseMonthYearFromValue(startStr);
                            const pE = parseMonthYearFromValue(endStr);
                            if (pS.month === pE.month) {
                              const is2Y = (recItem?.package_name || "").toUpperCase().includes("2 YEAR") || (recItem?.package_name || "").toUpperCase().includes("TWOYEAR") || Number(recItem?.amount) >= 1200;
                              const numMonths = is2Y ? 24 : 12;
                              const addM = numMonths - 1;
                              const newEM = ((pS.month - 1 + addM) % 12) + 1;
                              const newEY = pS.year + Math.floor((pS.month - 1 + addM) / 12);
                              const mName = MONTH_OPTIONS.find(m => m.value === newEM)?.label?.slice(0, 3) || `Month ${newEM}`;
                              endStr = `${mName} ${newEY}`;
                            }

                            return `${startStr} ➔ ${endStr}`;
                          };

                          return (
                            <tr 
                              key={rec.id || idx} 
                              style={{ 
                                borderBottom: "1px solid #f1f5f9",
                                backgroundColor: isCurrentActive ? "#f0fdf4" : isUpcoming ? "#eff6ff" : (idx % 2 === 0 ? "#ffffff" : "#fafafa")
                              }}
                            >
                              <td style={{ padding: "12px 14px", fontWeight: "600", color: "#64748b" }}>{idx + 1}</td>
                              <td style={{ padding: "12px 14px", fontWeight: "600", color: "#1e293b" }}>
                                {fmtDate(rec.receipt_date || rec.created_at)}
                              </td>
                              <td style={{ padding: "12px 14px", fontWeight: "700", color: "#5d1d2b" }}>
                                {rec.order_id || rec.payment_id || `VS-RCP-${String(rec.id).padStart(5, "0")}`}
                              </td>
                              <td style={{ padding: "12px 14px" }}>
                                <span style={{ fontWeight: "700", color: isCurrentActive ? "#15803d" : isUpcoming ? "#1d4ed8" : "#334155" }}>
                                  {rec.package_name || "Subscription"}
                                </span>
                              </td>
                              <td style={{ padding: "12px 14px", fontWeight: "800", color: "#0f172a" }}>
                                ₹{parseFloat(rec.amount || 0).toLocaleString("en-IN")}
                              </td>
                              <td style={{ padding: "12px 14px", color: "#475569" }}>
                                {fmtHistoryPeriod(rec.start_date, rec.end_date, rec)}
                              </td>
                              <td style={{ padding: "12px 14px" }}>
                                <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}>
                                  {rec.payment_mode || "Cash"}
                                </span>
                              </td>
                              <td style={{ padding: "12px 14px" }}>
                                {isUpcoming ? (
                                  <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                    ⏳ Upcoming
                                  </span>
                                ) : isCurrentActive ? (
                                  <span style={{ background: "#dcfce7", color: "#166534", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "800", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                    ● Active
                                  </span>
                                ) : (
                                  <span style={{ background: "#f1f5f9", color: "#64748b", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "600" }}>
                                    Expired
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: "12px 14px", textAlign: "center" }}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setViewingReceiptSub({
                                      ...historySub,
                                      ...rec,
                                      receipt_number: rec.order_id || rec.payment_id || `VS-RCP-${String(rec.id).padStart(5, "0")}`,
                                      receipt_date: rec.receipt_date || rec.created_at,
                                      package_name: rec.package_name,
                                      amount: rec.amount,
                                      start_date: rec.start_date,
                                      end_date: rec.end_date,
                                      address: historySub?.address || "",
                                      city: historySub?.city || "",
                                      pincode: historySub?.pincode || "",
                                      user_name: rec.user_name || historySub?.user_name,
                                      user_email: historySub?.user_email,
                                      user_phone: historySub?.user_phone
                                    });
                                  }}
                                  style={{
                                    background: "#fff",
                                    border: "1.5px solid #5d1d2b",
                                    color: "#5d1d2b",
                                    borderRadius: "6px",
                                    padding: "4px 10px",
                                    fontSize: "12px",
                                    fontWeight: "750",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px"
                                  }}
                                  title="View / Print Receipt Voucher for this Renewal"
                                >
                                  <i className="bi bi-file-earmark-text"></i> View Receipt
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", background: "#ffffff" }}>
              <button 
                type="button" 
                onClick={() => setHistorySub(null)}
                style={{ background: "#3b0f15", color: "#ffffff", border: "none", padding: "8px 24px", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer" }}
              >
                Close History
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ================= IN-PERSON SUBSCRIBER & RENEWAL MODAL ================= */}
      {isManualModalOpen && createPortal(
        <div 
          className="manual-sub-modal-overlay" 
          onClick={() => !manualLoading && setIsManualModalOpen(false)}
        >
          <div className="manual-sub-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* MODAL HEADER */}
            <div className="manual-modal-header">
              <div className="manual-header-title-box">
                <span className="manual-header-badge">IN-PERSON COUNTER DESK</span>
                <h3>
                  <i className="bi bi-person-vcard-fill"></i> Manual Subscriber & Renewal
                </h3>
                <p>Register walk-in subscriber or extend membership with Month & Year period issues</p>
              </div>
              <button 
                type="button" 
                className="manual-modal-close-btn" 
                onClick={() => !manualLoading && setIsManualModalOpen(false)}
                disabled={manualLoading}
              >
                &times;
              </button>
            </div>

            {/* IF SUCCESS DATA IS PRESENT: SHOW CELEBRATION / CREDENTIAL CARD */}
            {manualSuccessData ? (
              <div className="manual-success-container">
                <div className="manual-success-icon-badge">
                  <i className="bi bi-check-circle-fill"></i>
                </div>
                <h3>
                  {manualSuccessData.type === "create" ? "Subscriber Successfully Registered!" : "Subscription Successfully Renewed!"}
                </h3>
                <p className="manual-success-subtext">
                  {manualSuccessData.type === "create" 
                    ? "The new subscriber has been added to the database and can log in immediately."
                    : "The membership validity has been extended and receipt history recorded."}
                </p>

                <div className="manual-credential-card">
                  <div className="manual-card-row">
                    <span className="card-lbl">Subscription ID</span>
                    <strong className="card-val-highlight">{manualSuccessData.login_id}</strong>
                  </div>
                  <div className="manual-card-row">
                    <span className="card-lbl">Subscriber Name</span>
                    <strong className="card-val">{manualSuccessData.user_name}</strong>
                  </div>
                  {manualSuccessData.type === "create" && (
                    <div className="manual-card-row login-pass-row">
                      <span className="card-lbl">Temporary Password</span>
                      <strong className="card-val-pass">
                        {manualSuccessData.temp_password || manualSuccessData.phone}
                      </strong>
                    </div>
                  )}
                  <div className="manual-card-row">
                    <span className="card-lbl">Receipt Number</span>
                    <span className="card-val receipt-chip">{manualSuccessData.receipt_number}</span>
                  </div>
                  <div className="manual-card-row">
                    <span className="card-lbl">Active Period</span>
                    <span className="card-val period-chip">
                      {manualSuccessData.start_period} ➔ {manualSuccessData.end_period}
                    </span>
                  </div>
                  {manualSuccessData.plan_title && (
                    <div className="manual-card-row">
                      <span className="card-lbl">Selected Plan</span>
                      <span className="card-val">{manualSuccessData.plan_title}</span>
                    </div>
                  )}
                </div>

                {manualSuccessData.type === "create" && (
                  <div className="manual-credentials-help-box">
                    <i className="bi bi-info-circle-fill"></i>
                    <div>
                      <strong>Login Credentials for Subscriber:</strong>
                      <p>
                        Username: <strong>{manualSuccessData.login_id}</strong> &nbsp;|&nbsp; Password: <strong>{manualSuccessData.temp_password || manualSuccessData.phone}</strong>
                      </p>
                      <button
                        type="button"
                        className="btn-copy-creds"
                        onClick={() => {
                          const text = `Vaithikasri Subscription Credentials:\nUsername (Subscription ID): ${manualSuccessData.login_id}\nPassword (Phone Number): ${manualSuccessData.temp_password || manualSuccessData.phone}\nValidity: ${manualSuccessData.start_period} to ${manualSuccessData.end_period}\nReceipt No: ${manualSuccessData.receipt_number}`;
                          navigator.clipboard.writeText(text);
                          showPopup("Credentials copied to clipboard!");
                        }}
                      >
                        <i className="bi bi-clipboard"></i> Copy Credentials to Share
                      </button>
                    </div>
                  </div>
                )}

                <div className="manual-success-actions">
                  <button
                    type="button"
                    className="btn-add-another"
                    onClick={() => {
                      setManualSuccessData(null);
                      setCreateForm(initialCreateForm);
                      setRenewForm(initialRenewForm);
                    }}
                  >
                    <i className="bi bi-plus-circle"></i> Add / Renew Another
                  </button>
                  <button
                    type="button"
                    className="btn-close-success"
                    onClick={() => setIsManualModalOpen(false)}
                  >
                    Done & Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* TAB SWITCHER */}
                <div className="manual-modal-tabs">
                  <button
                    type="button"
                    className={`manual-modal-tab-btn ${manualTab === "create" ? "active" : ""}`}
                    onClick={() => setManualTab("create")}
                  >
                    <i className="bi bi-person-plus-fill"></i> Register New Subscriber
                  </button>
                  <button
                    type="button"
                    className={`manual-modal-tab-btn ${manualTab === "renew" ? "active" : ""}`}
                    onClick={() => setManualTab("renew")}
                  >
                    <i className="bi bi-arrow-repeat"></i> Renew Existing Subscriber
                  </button>
                </div>

                {/* TAB 1: CREATE NEW SUBSCRIBER */}
                {manualTab === "create" && (
                  <form onSubmit={handleManualCreateSubmit} className="manual-modal-form">
                    <div className="manual-form-body-scroll">
                      {/* SECTION 1: SUBSCRIBER DETAILS */}
                      <div className="manual-form-section">
                        <div className="section-title">
                          <i className="bi bi-person-fill"></i> Member Information
                        </div>
                        <div className="manual-form-grid">
                          <div className="manual-field-group">
                            <label>Full Name <span className="req">*</span></label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. R. Subramanian"
                              value={createForm.name}
                              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                            />
                          </div>

                          <div className="manual-field-group">
                            <label>Mobile / WhatsApp Phone <span className="req">*</span></label>
                            <input
                              type="tel"
                              required
                              maxLength="15"
                              placeholder="e.g. 9840123456"
                              value={createForm.phone}
                              onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value.replace(/[^\d+]/g, "") })}
                            />
                            <span className="field-hint">
                              <i className="bi bi-shield-lock"></i> Used as their initial login password
                            </span>
                          </div>

                          {existingActiveSubscriberByPhone && (
                            <div className="manual-phone-warning-banner full-width">
                              <div className="warning-banner-text">
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                <div>
                                  <strong>Active Subscriber Found with this Phone Number!</strong>
                                  <p>
                                    Member <strong>{existingActiveSubscriberByPhone.user_name || existingActiveSubscriberByPhone.name}</strong> ({existingActiveSubscriberByPhone.login_id || 'ID: ' + existingActiveSubscriberByPhone.id}) has an active subscription until <strong>{existingActiveSubscriberByPhone.end_month || existingActiveSubscriberByPhone.end_date || 'Active'}</strong>.
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn-switch-to-renew"
                                onClick={() => {
                                  handleSelectSubscriberForRenew(existingActiveSubscriberByPhone);
                                  setManualTab("renew");
                                }}
                              >
                                <i className="bi bi-arrow-repeat"></i> Switch to Renew This Member
                              </button>
                            </div>
                          )}

                          <div className="manual-field-group full-width">
                            <label>Email Address <span className="optional">(Optional)</span></label>
                            <input
                              type="email"
                              placeholder="e.g. member@gmail.com"
                              value={createForm.email}
                              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* SECTION 2: DISPATCH POSTAL ADDRESS */}
                      <div className="manual-form-section">
                        <div className="section-title">
                          <i className="bi bi-geo-alt-fill"></i> Magazine Dispatch Address
                        </div>
                        <div className="manual-form-grid">
                          <div className="manual-field-group full-width">
                            <label>Door No, Building, Street Address <span className="req">*</span></label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. New No. 488, Old No. 175, T.T.K. Road, Alwarpet"
                              value={createForm.address}
                              onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })}
                            />
                          </div>

                          <div className="manual-field-group">
                            <label>City / Town <span className="req">*</span></label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Chennai"
                              value={createForm.city}
                              onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })}
                            />
                          </div>

                          <div className="manual-field-group">
                            <label>State <span className="req">*</span></label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Tamil Nadu"
                              value={createForm.state}
                              onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })}
                            />
                          </div>

                          <div className="manual-field-group">
                            <label>Postal Pincode <span className="req">*</span></label>
                            <input
                              type="text"
                              required
                              maxLength="10"
                              placeholder="e.g. 600018"
                              value={createForm.pincode}
                              onChange={(e) => setCreateForm({ ...createForm, pincode: e.target.value.replace(/[^\d]/g, "") })}
                            />
                          </div>

                          <div className="manual-field-group">
                            <label>Country</label>
                            <input
                              type="text"
                              placeholder="e.g. India"
                              value={createForm.country}
                              onChange={(e) => {
                                const newCountry = e.target.value;
                                handleCreatePlanOrDurationChange(createForm.plan_id, createForm.duration_years, newCountry);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* SECTION 3: PLAN, DURATION & STRICT MONTH/YEAR PERIOD */}
                      <div className="manual-form-section">
                        <div className="section-title">
                          <i className="bi bi-journal-bookmark-fill"></i> Subscription Plan & Issue Period
                        </div>
                        <div className="manual-form-grid">
                          <div className="manual-field-group full-width">
                            <label>Magazine Plan <span className="req">*</span></label>
                            <select
                              required
                              value={createForm.plan_id}
                              onChange={(e) => handleCreatePlanOrDurationChange(e.target.value, createForm.duration_years)}
                            >
                              <option value="">-- Choose Subscription Plan --</option>
                              {plans.map((p) => {
                                const priceLabel = createForm.duration_years === 2 
                                  ? `(₹${p.annual_price || 0})` 
                                  : createForm.duration_years === 1 
                                  ? `(₹${p.monthly_price || 0})` 
                                  : `(1 Yr: ₹${p.monthly_price || 0} / 2 Yrs: ₹${p.annual_price || 0})`;
                                return (
                                  <option key={p.id} value={p.id}>
                                    Plan {p.id}: {p.monthly_title || p.annual_title} {priceLabel}
                                  </option>
                                );
                              })}
                            </select>
                          </div>

                          <div className="manual-field-group full-width">
                            <label>Duration Period <span className="req">*</span></label>
                            <div className="manual-duration-selector">
                              <button
                                type="button"
                                className={`duration-pill-btn ${createForm.duration_years === 1 ? "active" : ""}`}
                                onClick={() => handleCreatePlanOrDurationChange(createForm.plan_id, 1)}
                              >
                                <span className="pill-title">1 Year</span>
                                <span className="pill-sub">12 Magazine Issues</span>
                              </button>
                              <button
                                type="button"
                                className={`duration-pill-btn ${createForm.duration_years === 2 ? "active" : ""}`}
                                onClick={() => handleCreatePlanOrDurationChange(createForm.plan_id, 2)}
                              >
                                <span className="pill-title">2 Years</span>
                                <span className="pill-sub">24 Magazine Issues</span>
                              </button>
                            </div>
                          </div>

                          <div className="manual-field-group">
                            <label>Starting Issue Month <span className="req">*</span></label>
                            <select
                              value={createForm.start_month}
                              onChange={(e) => setCreateForm({ ...createForm, start_month: parseInt(e.target.value, 10) })}
                            >
                              {MONTH_OPTIONS.map((m) => (
                                <option key={m.value} value={m.value}>
                                  {m.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="manual-field-group">
                            <label>Starting Issue Year <span className="req">*</span></label>
                            <select
                              value={createForm.start_year}
                              onChange={(e) => setCreateForm({ ...createForm, start_year: parseInt(e.target.value, 10) })}
                            >
                              {availableYears.map((yr) => (
                                <option key={yr} value={yr}>
                                  {yr}
                                </option>
                              ))}
                              {[...Array(4)].map((_, i) => {
                                const yr = new Date().getFullYear() + 1 + i;
                                if (!availableYears.includes(yr)) {
                                  return (
                                    <option key={yr} value={yr}>
                                      {yr}
                                    </option>
                                  );
                                }
                                return null;
                              })}
                            </select>
                          </div>

                          {/* PERIOD DISPLAY BANNER */}
                          <div className="manual-field-group full-width">
                            <div className="period-calculated-banner">
                              <i className="bi bi-calendar-check-fill"></i>
                              <div>
                                <span className="period-tag">CALCULATED MAGAZINE ISSUE PERIOD</span>
                                <strong>
                                  {calculatePeriod(createForm.start_month, createForm.start_year, createForm.duration_years).displayString}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* SECTION 4: PAYMENT & IN-PERSON DETAILS */}
                      <div className="manual-form-section">
                        <div className="section-title">
                          <i className="bi bi-cash-stack"></i> Payment & Counter Details
                        </div>
                        <div className="manual-form-grid">
                          <div className="manual-field-group">
                            <label>Payment Mode <span className="req">*</span></label>
                            <select
                              value={createForm.payment_mode}
                              onChange={(e) => setCreateForm({ ...createForm, payment_mode: e.target.value })}
                            >
                              <option value="Cash / Counter">💵 Cash / Counter</option>
                              <option value="UPI / GPay / PhonePe">📱 UPI / GPay / PhonePe</option>
                              <option value="Cheque">🏦 Cheque</option>
                              <option value="Demand Draft (DD)">📄 Demand Draft (DD)</option>
                              <option value="Bank Transfer (NEFT/IMPS)">💳 Bank Transfer (NEFT/IMPS)</option>
                              <option value="Other">🏷️ Other</option>
                            </select>
                          </div>

                          <div className="manual-field-group">
                            <label>Amount Collected (₹) <span className="req">*</span></label>
                            <input
                              type="number"
                              step="0.01"
                              required
                              placeholder={createForm.plan_id && createForm.duration_years ? "e.g. 500" : "Choose Plan & Duration above"}
                              value={createForm.amount}
                              onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
                            />
                          </div>

                          <div className="manual-field-group full-width">
                            <label>Remarks / Notes <span className="optional">(Optional)</span></label>
                            <input
                              type="text"
                              placeholder="e.g. In-person counter payment, Alwarpet office"
                              value={createForm.remarks_1}
                              onChange={(e) => setCreateForm({ ...createForm, remarks_1: e.target.value })}
                            />
                          </div>

                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "9px 14px",
                            background: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            borderRadius: "8px",
                            color: "#166534",
                            fontSize: "12.5px",
                            fontWeight: "600",
                            marginTop: "12px"
                          }}>
                            <i className="bi bi-whatsapp" style={{ color: "#16a34a", fontSize: "16px" }}></i>
                            <span>Official WhatsApp confirmation with PDF receipt is automatically sent upon registration</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* MODAL FOOTER */}
                    <div className="manual-modal-footer">
                      <button
                        type="button"
                        className="btn-cancel-modal"
                        onClick={() => setIsManualModalOpen(false)}
                        disabled={manualLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-submit-modal"
                        disabled={manualLoading}
                      >
                        {manualLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Creating Subscriber...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-person-check-fill me-1"></i> Register & Issue Receipt
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* TAB 2: RENEW EXISTING SUBSCRIBER */}
                {manualTab === "renew" && (
                  <form onSubmit={handleManualRenewSubmit} className="manual-modal-form">
                    <div className="manual-form-body-scroll">
                      {/* SEARCH OR SELECTED SUBSCRIBER */}
                      {!renewForm.subscriber ? (
                        <div className="manual-form-section">
                          <div className="section-title">
                            <i className="bi bi-search"></i> Search Subscriber to Renew
                          </div>
                          <p className="search-instruction">
                            Search by <strong>Subscriber Name</strong>, <strong>Mobile Number</strong>, or <strong>Subscription ID</strong>:
                          </p>

                          <div className="manual-search-input-wrapper">
                            <i className="bi bi-search search-icon"></i>
                            <input
                              type="text"
                              autoFocus
                              className="admin-search-input"
                              style={{ paddingLeft: "48px" }}
                              placeholder="Type Name, Mobile Number, or VS ID..."
                              value={renewSearchQuery}
                              onChange={(e) => setRenewSearchQuery(e.target.value)}
                            />
                            {renewSearchQuery && (
                              <button
                                type="button"
                                className="search-clear-btn"
                                onClick={() => setRenewSearchQuery("")}
                              >
                                &times;
                              </button>
                            )}
                          </div>

                          {/* SEARCH RESULTS DROPDOWN */}
                          {renewSearchQuery.trim().length >= 2 && (
                            <div className="manual-search-results-list">
                              {renewSearchResults.length === 0 ? (
                                <div className="no-search-results">
                                  <i className="bi bi-exclamation-circle"></i> No subscriber found matching "{renewSearchQuery}"
                                </div>
                              ) : (
                                renewSearchResults.map((sub) => {
                                  const isActive = sub.status === "paid" && (!sub.end_date || new Date(sub.end_date) >= new Date());
                                  return (
                                    <div
                                      key={sub.id}
                                      className="manual-search-result-card"
                                      onClick={() => handleSelectSubscriberForRenew(sub)}
                                    >
                                      <div className="result-main-col">
                                        <div className="result-name-row">
                                          <strong>{sub.user_name || "Unknown Subscriber"}</strong>
                                          <span className="result-sub-id">{sub.login_id || `ID: ${sub.id}`}</span>
                                        </div>
                                        <div className="result-details-row">
                                          <span><i className="bi bi-telephone"></i> {sub.user_phone || sub.phone || "No Phone"}</span>
                                          {(sub.city || sub.user_city) && (
                                            <span><i className="bi bi-geo-alt"></i> {sub.city || sub.user_city}</span>
                                          )}
                                          <span><i className="bi bi-box-seam"></i> Plan {sub.plan_id || "N/A"}</span>
                                        </div>
                                      </div>

                                      <div className="result-expiry-col">
                                        <span className={`expiry-badge ${isActive ? "active" : "expired"}`}>
                                          {isActive ? "Active" : "Expired"}
                                        </span>
                                        <span className="expiry-date-str">
                                          {sub.end_month ? sub.end_month : sub.end_date ? new Date(sub.end_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "No Expiry"}
                                        </span>
                                        <button type="button" className="btn-select-sub">
                                          Select ➔
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* SELECTED SUBSCRIBER CARD */}
                          <div className="selected-sub-card">
                            <div className="sub-card-header">
                              <div className="sub-id-name">
                                <span className="sub-id-pill">{renewForm.subscriber.login_id || `ID: ${renewForm.subscriber.id}`}</span>
                                <h4>{renewForm.subscriber.user_name}</h4>
                              </div>
                              <button
                                type="button"
                                className="btn-change-sub"
                                onClick={() => setRenewForm({ ...renewForm, subscriber: null })}
                              >
                                <i className="bi bi-arrow-left-right"></i> Choose Different Member
                              </button>
                            </div>

                            <div className="sub-card-details-grid">
                              <div>
                                <span className="lbl">Mobile / WhatsApp</span>
                                <strong>{renewForm.subscriber.user_phone || renewForm.subscriber.phone || "—"}</strong>
                              </div>
                              <div>
                                <span className="lbl">City / Location</span>
                                <strong>{renewForm.subscriber.city || renewForm.subscriber.user_city || "—"}</strong>
                              </div>
                              <div>
                                <span className="lbl">Current / Previous Expiry</span>
                                <strong className="expiry-val">
                                  {renewForm.subscriber.end_month || 
                                    (renewForm.subscriber.end_date ? new Date(renewForm.subscriber.end_date).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "N/A")}
                                </strong>
                              </div>
                              <div>
                                <span className="lbl">Previous Plan</span>
                                <strong style={{ color: "#7c2d12" }}>
                                  {renewForm.subscriber.package_name || (renewForm.subscriber.plan_id ? `Plan ${renewForm.subscriber.plan_id}` : "—")}
                                </strong>
                              </div>
                            </div>
                          </div>

                          {/* RENEWAL PLAN & DURATION */}
                          <div className="manual-form-section">
                            <div className="section-title">
                              <i className="bi bi-journal-bookmark-fill"></i> Renewal Plan & Magazine Issues Period
                            </div>
                            <div className="manual-form-grid">
                              <div className="manual-field-group full-width">
                                <label>Renewal Magazine Plan <span className="req">*</span></label>
                                <select
                                  required
                                  value={renewForm.plan_id}
                                  onChange={(e) => handleRenewPlanOrDurationChange(e.target.value, renewForm.duration_years)}
                                >
                                  <option value="">-- Choose Renewal Plan --</option>
                                  {plans.map((p) => {
                                    const priceLabel = renewForm.duration_years === 2 
                                      ? `(₹${p.annual_price || 0})` 
                                      : renewForm.duration_years === 1 
                                      ? `(₹${p.monthly_price || 0})` 
                                      : `(1 Yr: ₹${p.monthly_price || 0} / 2 Yrs: ₹${p.annual_price || 0})`;
                                    return (
                                      <option key={p.id} value={p.id}>
                                        Plan {p.id}: {p.monthly_title || p.annual_title} {priceLabel}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              <div className="manual-field-group full-width">
                                <label>Renewal Duration Period <span className="req">*</span></label>
                                <div className="manual-duration-selector">
                                  <button
                                    type="button"
                                    className={`duration-pill-btn ${renewForm.duration_years === 1 ? "active" : ""}`}
                                    onClick={() => handleRenewPlanOrDurationChange(renewForm.plan_id, 1)}
                                  >
                                    <span className="pill-title">1 Year Extension</span>
                                    <span className="pill-sub">12 Magazine Issues</span>
                                  </button>
                                  <button
                                    type="button"
                                    className={`duration-pill-btn ${renewForm.duration_years === 2 ? "active" : ""}`}
                                    onClick={() => handleRenewPlanOrDurationChange(renewForm.plan_id, 2)}
                                  >
                                    <span className="pill-title">2 Years Extension</span>
                                    <span className="pill-sub">24 Magazine Issues</span>
                                  </button>
                                </div>
                              </div>

                              <div className="manual-field-group">
                                <label>Renewal Start Month <span className="req">*</span></label>
                                <select
                                  value={renewForm.start_month}
                                  onChange={(e) => setRenewForm({ ...renewForm, start_month: parseInt(e.target.value, 10) })}
                                >
                                  {MONTH_OPTIONS.map((m) => (
                                    <option key={m.value} value={m.value}>
                                      {m.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="manual-field-group">
                                <label>Renewal Start Year <span className="req">*</span></label>
                                <select
                                  value={renewForm.start_year}
                                  onChange={(e) => setRenewForm({ ...renewForm, start_year: parseInt(e.target.value, 10) })}
                                >
                                  {availableYears.map((yr) => (
                                    <option key={yr} value={yr}>
                                      {yr}
                                    </option>
                                  ))}
                                  {[...Array(4)].map((_, i) => {
                                    const yr = new Date().getFullYear() + 1 + i;
                                    if (!availableYears.includes(yr)) {
                                      return (
                                        <option key={yr} value={yr}>
                                          {yr}
                                        </option>
                                      );
                                    }
                                    return null;
                                  })}
                                </select>
                              </div>

                              {/* PERIOD DISPLAY BANNER */}
                              <div className="manual-field-group full-width">
                                <div className="period-calculated-banner renew">
                                  <i className="bi bi-calendar-check-fill"></i>
                                  <div>
                                    <span className="period-tag">EXTENDED MAGAZINE ISSUE PERIOD</span>
                                    <strong>
                                      {calculatePeriod(renewForm.start_month, renewForm.start_year, renewForm.duration_years).displayString}
                                    </strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* PAYMENT DETAILS */}
                          <div className="manual-form-section">
                            <div className="section-title">
                              <i className="bi bi-cash-stack"></i> Renewal Payment Details
                            </div>
                            <div className="manual-form-grid">
                              <div className="manual-field-group">
                                <label>Payment Mode <span className="req">*</span></label>
                                <select
                                  value={renewForm.payment_mode}
                                  onChange={(e) => setRenewForm({ ...renewForm, payment_mode: e.target.value })}
                                >
                                  <option value="Cash / Counter">💵 Cash / Counter</option>
                                  <option value="UPI / GPay / PhonePe">📱 UPI / GPay / PhonePe</option>
                                  <option value="Cheque">🏦 Cheque</option>
                                  <option value="Demand Draft (DD)">📄 Demand Draft (DD)</option>
                                  <option value="Bank Transfer (NEFT/IMPS)">💳 Bank Transfer (NEFT/IMPS)</option>
                                  <option value="Other">🏷️ Other</option>
                                </select>
                              </div>

                              <div className="manual-field-group">
                                <label>Amount Collected (₹) <span className="req">*</span></label>
                                <input
                                  type="number"
                                  step="0.01"
                                  required
                                  placeholder={renewForm.plan_id && renewForm.duration_years ? "e.g. 500" : "Choose Plan & Duration above"}
                                  value={renewForm.amount}
                                  onChange={(e) => setRenewForm({ ...renewForm, amount: e.target.value })}
                                />
                              </div>

                              <div className="manual-field-group full-width">
                                <label>Remarks / Notes <span className="optional">(Optional)</span></label>
                                <input
                                  type="text"
                                  placeholder="e.g. In-person counter renewal"
                                  value={renewForm.remarks_1}
                                  onChange={(e) => setRenewForm({ ...renewForm, remarks_1: e.target.value })}
                                />
                              </div>

                               <div style={{
                                 display: "flex",
                                 alignItems: "center",
                                 gap: "8px",
                                 padding: "9px 14px",
                                 background: "#f0fdf4",
                                 border: "1px solid #bbf7d0",
                                 borderRadius: "8px",
                                 color: "#166534",
                                 fontSize: "12.5px",
                                 fontWeight: "600",
                                 marginTop: "12px"
                               }}>
                                 <i className="bi bi-whatsapp" style={{ color: "#16a34a", fontSize: "16px" }}></i>
                                 <span>Official WhatsApp renewal confirmation with PDF receipt is automatically sent to (+91 {renewForm.subscriber?.user_phone || renewForm.subscriber?.phone || ""})</span>
                               </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* MODAL FOOTER */}
                    {renewForm.subscriber && (
                      <div className="manual-modal-footer">
                        <button
                          type="button"
                          className="btn-cancel-modal"
                          onClick={() => setIsManualModalOpen(false)}
                          disabled={manualLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn-submit-modal"
                          disabled={manualLoading}
                        >
                          {manualLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Renewing Subscription...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-arrow-repeat me-1"></i> Confirm Renewal & Issue Receipt
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </form>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* FLOATING EXPORT PROGRESS TOAST */}
      {exportLoading && (
        <div className="export-toast-banner">
          <span className="spinner-border spinner-border-sm text-light"></span>
          <span>
            Preparing Excel file for {exportTypeLoading === "active" ? "Active Subscribers" : "All Subscribers"}... Download will start automatically.
          </span>
        </div>
      )}
    </div>
  );
};

export default AdminSubscribers;

